/**
 * sl-kypm.5.3 + sl-qndg.5.3: Import setup + confirmation UI.
 *
 * Shows the template impact preview and opens a confirmation dialog for the
 * actual install. Produces ConcretizationBindings for the backend.
 *
 * sl-qndg.5.3: Consumes `GET /api/marketplace/templates/:id/prerequisites`
 * (the SSOT that the concretizer + binding-reference-validator use) so the
 * wizard shows the real pre-install surface — including unknown credential
 * references the template author drifted from their declared requirements.
 * Credential prerequisites are shown as install impact. Concrete credential
 * mapping is resolved through the backend install contract.
 *
 * SOLID:
 * - S: Only import setup UI — no install logic (callback-based)
 * - O: Preview and confirmation sections are data-driven by prerequisites
 * - D: Depends on props (template data in, bindings out)
 */
import { Menu, MenuItem, MenuTrigger, Modal } from '@affine/component';
import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { GraphQLService, workspaceApiFetch } from '@affine/core/modules/cloud';
import { type DocRecord, DocsService } from '@affine/core/modules/doc';
import { GuardService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  DOC_SIZE_LIMIT_BYTES,
  formatDocumentSizeScore,
} from '@affine/core/modules/workspace/views/document-size-score';
import { customAppsQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import * as Popover from '@radix-ui/react-popover';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../../../modules/streamlain/services/database-discovery';
import { AutoMatchDialog } from '../../../modules/streamlain/sidebar/components/auto-match-dialog';
import type { AutoMatchSuggestion } from '../../../modules/streamlain/sidebar/utils/auto-match-fields';
import {
  type AutomationDatabaseBindings,
  type AutomationDatabaseColumnBinding,
  type AutomationDatabaseSurfaceBinding,
  createAutomationDatabaseBindings,
  getAutomationDatabaseCreateFlags,
  hasExistingDatabaseBindings,
  updateAutomationDatabaseBindingTargetDoc,
} from './automation-database-bindings';
import { pickMarketplaceChangelog } from './changelog-i18n';
import { DocTemplatePreviewPanel } from './doc-template-preview';
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  DownloadIcon,
  PlayIcon,
  StarIcon,
} from './icons';
import type { InstalledTemplateManagement } from './installed-template-management';
import { getMarketplaceCategoryLabel } from './marketplace-categories';
import { openMarketplaceTemplateInNewTab } from './marketplace-install-state';
import { PreviewImpactCard } from './preview-impact-card';
import * as styles from './styles.css';
import { formatCount, formatRating, TYPE_CONFIG } from './template-card';
import { isRepeatableBlueprint } from './template-kind';
import {
  type InstalledTemplateInfo,
  type MarketplaceTemplate,
} from './use-marketplace-api';
import type {
  ColumnPreview,
  ConfigPreviewItem,
  TemplateDependencyInfo,
  TemplateInstallPermissionRequirement,
  TemplatePrerequisites,
} from './use-template-prerequisites';
import { useTemplatePrerequisites } from './use-template-prerequisites';

// =============================================================================
// Types
// =============================================================================

/** Bindings produced by the wizard, sent to the concretizer API. */
export interface ConcretizationBindings {
  credentialMappings: Record<string, string>;
  configValues: Record<string, string>;
  columnMappings?: Record<string, string>;
  confirmedDependencies?: MarketplaceDependencyConfirmation[];
  appImportOptions?: {
    docIdReplacements: Record<string, string>;
    blockIdReplacements: Record<string, string>;
  };
  artifactTargets?: Array<{
    sourceDocId?: string;
    sourceBlockId: string;
    targetDocId: string;
    targetBlockId: string;
    resourceKey?: string;
    ownership?: 'owned' | 'shared' | 'external';
  }>;
  agentContextGrants?: {
    memoryEnabled?: boolean;
    knowledgeGraphEnabled?: boolean;
    documentIds?: string[];
  };
  /** sl-mkx.5.3: installer-supplied trigger values keyed by fieldRef. */
  triggerValues?: Record<string, string>;
}

export interface MarketplaceDependencyConfirmation {
  templateId: string;
  versionNumber: number;
  canonicalKey: string;
  majorVersion: number;
  semanticVersion: string;
  contractHash: string;
}

/**
 * sl-mkx.3.2: Install-time options collected by the wizard for automation
 * templates. Emitted alongside ConcretizationBindings so the backend install
 * flow can create input/output databases and route the install into an
 * existing doc instead of a fresh one.
 */
export interface InstallOptions {
  targetVersionNumber?: number;
  /** Target doc id. `undefined` means "create a new doc". */
  targetDocId?: string;
  createInputDbs?: boolean;
  createOutputDbs?: boolean;
  databaseBindings?: AutomationDatabaseBindings;
  appBlockTarget?:
    | {
        operationId: string;
        kind: 'existing_app';
        customAppId: string;
      }
    | {
        operationId: string;
        kind: 'new_app';
        title: string;
        appKind: 'workspace';
      };
  appBlockTargetRootRoutePath?: string;
  appBlockDatabaseMappingDecisions?: Record<
    string,
    { action: 'create_new' } | { action: 'use_existing'; target: string }
  >;
  appBlockConfirmedPreflightDigest?: string;
}

interface AppBlockInstantiationPreflight {
  canInstantiate: boolean;
  conflicts: string[];
  preflightDigest: string;
  plan: {
    targetRootRoutePath: string;
    routePaths: string[];
    dataSourceMappings: Array<{
      source: string;
      target: string;
      action: string;
      conflictReason?: string;
    }>;
    automationMappings: Array<{
      source: string;
      target: string;
      action: string;
    }>;
    credentialMappings: Array<{
      source: string;
      target: string;
      action: string;
    }>;
  };
}

// =============================================================================
// Step 1: Template Preview
// =============================================================================

type MarketplaceI18n = ReturnType<typeof useI18n>;

function marketplaceCopy(
  t: MarketplaceI18n,
  key: string,
  fallback: string,
  options?: Record<string, string>
): string {
  const fn = (t as unknown as Record<string, unknown>)[key];
  if (typeof fn !== 'function') return fallback;
  return (fn as (options?: Record<string, string>) => string)(options);
}

interface PreviewListItem {
  id: string;
  title: string;
  meta?: ReactNode;
  badge?: ReactNode;
  action?: ReactNode;
  details?: ReactNode;
}

interface DatabaseColumnGroup {
  key: string;
  label: string;
  role?: ColumnPreview['databaseRole'];
  columns: ColumnPreview[];
}

type InstallProgressStep =
  | 'preparing'
  | 'importing-docs'
  | 'creating-workflow'
  | 'finalizing'
  | 'done'
  | 'error';

const INSTALL_STEP_LABEL_KEYS: Record<InstallProgressStep, string> = {
  preparing: 'com.streamlain.marketplace.browse.install.label.preparing',
  'importing-docs':
    'com.streamlain.marketplace.browse.install.label.importing-docs',
  'creating-workflow':
    'com.streamlain.marketplace.browse.install.label.creating-workflow',
  finalizing: 'com.streamlain.marketplace.browse.install.label.finalizing',
  done: 'com.streamlain.marketplace.browse.install.label.done',
  error: 'com.streamlain.marketplace.browse.install.label.error',
};

function templateTypeLabel(type: string, t: MarketplaceI18n): string {
  const key =
    `com.streamlain.marketplace.card.type.${type}` as keyof MarketplaceI18n;
  const candidate = t[key];
  return typeof candidate === 'function' ? candidate() : type;
}

function usesInstallTargetDoc(template: MarketplaceTemplate): boolean {
  return (
    template.templateType === 'doc' ||
    template.templateType === 'app' ||
    template.templateType === 'automation'
  );
}

function getInstallSuccessDetail(
  template: MarketplaceTemplate,
  installResult: ImportInstallResult | null,
  t: MarketplaceI18n
): string {
  if (template.templateType === 'app_block') {
    return marketplaceCopy(
      t,
      'com.streamlain.marketplace.import.confirm.success-detail.app-block',
      'Der App-Block ist jetzt Teil der gewählten App.'
    );
  }
  if (template.templateType === 'workflow') {
    return marketplaceCopy(
      t,
      'com.streamlain.marketplace.import.confirm.success-detail.workflow',
      'Workflow wurde erstellt.'
    );
  }
  if (template.templateType === 'step') {
    return marketplaceCopy(
      t,
      'com.streamlain.marketplace.import.confirm.success-detail.step',
      'Step ist im Builder verfügbar.'
    );
  }
  if (template.templateType === 'automation') {
    return marketplaceCopy(
      t,
      'com.streamlain.marketplace.import.confirm.success-detail.automation',
      'Automation wurde erstellt.'
    );
  }
  return t['com.streamlain.marketplace.import.confirm.success-detail']({
    docs: String(installResult?.installedDocIds?.length ?? 0),
  });
}

function resolveTemplateLatestVersion(template: MarketplaceTemplate): number {
  const latestFromVersions = Math.max(
    0,
    ...(template.versions ?? []).map(version => version.versionNumber)
  );
  if (latestFromVersions > 0) return latestFromVersions;

  if (
    typeof template.latestVersion === 'number' &&
    Number.isInteger(template.latestVersion) &&
    template.latestVersion > 0
  ) {
    return template.latestVersion;
  }
  return 1;
}

function buildInstallVersionOptions(input: {
  template: MarketplaceTemplate;
  latestVersion: number;
}): number[] {
  const versionNumbers = new Set<number>();
  for (const version of input.template.versions ?? []) {
    if (
      Number.isInteger(version.versionNumber) &&
      version.versionNumber > 0 &&
      version.versionNumber <= input.latestVersion
    ) {
      versionNumbers.add(version.versionNumber);
    }
  }
  const options = [...versionNumbers].sort((a, b) => b - a);
  return options.length > 0 ? options : [input.latestVersion];
}

function installVersionLabel(
  t: MarketplaceI18n,
  template: MarketplaceTemplate,
  versionNumber: number,
  latestVersion: number
): string {
  const semanticVersion = requireTemplateSemanticVersion(
    template,
    versionNumber
  );
  return versionNumber === latestVersion
    ? t['com.streamlain.marketplace.import.version.latest']({
        version: semanticVersion,
      })
    : t['com.streamlain.marketplace.import.version.option']({
        version: semanticVersion,
      });
}

function requireTemplateSemanticVersion(
  template: MarketplaceTemplate,
  versionNumber: number
): string {
  const semanticVersion = getTemplateVersionMetadata(
    template,
    versionNumber
  )?.semanticVersion;
  if (typeof semanticVersion === 'string' && semanticVersion.length > 0) {
    return semanticVersion;
  }
  throw new Error(
    `Marketplace template ${template.id} is missing semanticVersion for version ${versionNumber}`
  );
}

function getTemplateVersionMetadata(
  template: MarketplaceTemplate,
  versionNumber: number,
  picker: ReturnType<typeof useLocalePicker> = null
): { semanticVersion: string; changelog: string | null } | null {
  const version = template.versions?.find(
    version => version.versionNumber === versionNumber
  );
  if (!version || version.semanticVersion.length === 0) return null;
  const changelog = pickMarketplaceChangelog(version, picker).trim();
  return {
    semanticVersion: version.semanticVersion,
    changelog: changelog.length > 0 ? changelog : null,
  };
}

function requireInstalledLatestSemanticVersion(
  installedInfo: InstalledTemplateInfo
): string {
  if (
    typeof installedInfo.latestSemanticVersion === 'string' &&
    installedInfo.latestSemanticVersion.length > 0
  ) {
    return installedInfo.latestSemanticVersion;
  }
  throw new Error(
    `Marketplace install ${installedInfo.installId} is missing latestSemanticVersion`
  );
}

function PreviewList({
  items,
  empty,
}: {
  items: readonly PreviewListItem[];
  empty: ReactNode;
}) {
  if (items.length === 0) {
    return <div className={styles.importEmptyLine}>{empty}</div>;
  }

  return (
    <div className={styles.importList}>
      {items.map(item => (
        <div key={item.id} className={styles.importListItem}>
          <div className={styles.importListMain}>
            <span className={styles.importListTitle}>{item.title}</span>
            {item.meta && (
              <span className={styles.importListMeta}>{item.meta}</span>
            )}
          </div>
          {item.badge || item.action ? (
            <div className={styles.importListActions}>
              {item.badge}
              {item.action}
            </div>
          ) : null}
          {item.details ? (
            <div className={styles.importListDetails}>{item.details}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

interface InstallTargetDocCandidate {
  id: string;
  title: string;
  updatedAt?: number;
}

interface InstallTargetDocOption extends InstallTargetDocCandidate {
  sizeBytes: number | null;
}

interface StoreClientWithDocLookup {
  waitForLocalDocStorageConnected?: () => Promise<void>;
  getDoc?: (
    docId: string
  ) => Promise<{ bin?: { byteLength?: number; length?: number } } | null>;
}

function docsToTargetCandidates(
  docs: readonly DocRecord[]
): InstallTargetDocCandidate[] {
  return docs
    .map(doc => ({
      id: doc.id,
      title: doc.title$.value.trim(),
      isTrash: doc.trash$.value,
      updatedAt: doc.updatedAt$.value,
    }))
    .filter(doc => !doc.isTrash)
    .map(({ id, title, updatedAt }) => ({ id, title, updatedAt }));
}

async function readLocalDocSize(
  client: StoreClientWithDocLookup,
  docId: string
): Promise<number | null> {
  if (!client.getDoc) return null;
  try {
    await client.waitForLocalDocStorageConnected?.();
    const record = await client.getDoc(docId);
    return record?.bin?.byteLength ?? record?.bin?.length ?? 0;
  } catch {
    return null;
  }
}

function InstallTargetPicker({
  targetDocId,
  onTargetDocChange,
  disabled,
}: {
  targetDocId: string | undefined;
  onTargetDocChange: (docId: string | undefined) => void;
  disabled: boolean;
}) {
  const t = useI18n();
  const docsService = useService(DocsService);
  const guardService = useService(GuardService);
  const workspace = useService(WorkspaceService).workspace;
  const allDocs = useLiveData(docsService.list.docs$);
  const candidateDocs$ = useMemo(
    () => LiveData.computed(() => docsToTargetCandidates(allDocs)),
    [allDocs]
  );
  const candidateDocs = useLiveData(candidateDocs$);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [isCheckingDocs, setCheckingDocs] = useState(false);
  const [options, setOptions] = useState<InstallTargetDocOption[]>([]);

  useEffect(() => {
    let disposed = false;
    const client = workspace.engine.client as StoreClientWithDocLookup;

    async function loadWritableDocs() {
      setCheckingDocs(true);
      const next: InstallTargetDocOption[] = [];

      for (const doc of candidateDocs) {
        const canWrite =
          workspace.flavour === 'local'
            ? true
            : await guardService.can('Doc_Update', doc.id).catch(() => false);
        if (!canWrite) continue;

        const sizeBytes = await readLocalDocSize(client, doc.id);
        if (sizeBytes !== null && sizeBytes >= DOC_SIZE_LIMIT_BYTES) continue;

        next.push({ ...doc, sizeBytes });
      }

      next.sort((a, b) => {
        const byUpdated = (b.updatedAt ?? 0) - (a.updatedAt ?? 0);
        return byUpdated || a.title.localeCompare(b.title);
      });

      if (!disposed) {
        setOptions(next);
        setCheckingDocs(false);
      }
    }

    void loadWritableDocs();
    return () => {
      disposed = true;
    };
  }, [candidateDocs, guardService, workspace]);

  useEffect(() => {
    if (!targetDocId) return;
    if (options.some(option => option.id === targetDocId)) return;
    onTargetDocChange(undefined);
  }, [onTargetDocChange, options, targetDocId]);

  const selectedDoc = targetDocId
    ? options.find(option => option.id === targetDocId)
    : null;
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredOptions = normalizedQuery
    ? options.filter(option =>
        option.title.toLocaleLowerCase().includes(normalizedQuery)
      )
    : options;
  const selectedLabel = selectedDoc?.title
    ? selectedDoc.title
    : targetDocId
      ? t['com.streamlain.marketplace.import.target.selected']()
      : t['com.streamlain.marketplace.import.target.new']();

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.installTargetTrigger}
          disabled={disabled}
          aria-label={t['com.streamlain.marketplace.import.target.label']()}
        >
          <span className={styles.installTargetTriggerLabel}>
            {selectedLabel}
          </span>
          <span className={styles.installTargetTriggerChevron}>▾</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={12}
          className={styles.installTargetPopover}
        >
          <input
            className={styles.installTargetSearch}
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t['com.streamlain.marketplace.import.target.search']()}
            autoFocus
          />
          <div className={styles.installTargetList}>
            <button
              type="button"
              className={styles.installTargetItem}
              data-selected={targetDocId ? undefined : true}
              onClick={() => {
                onTargetDocChange(undefined);
                setOpen(false);
              }}
            >
              <span className={styles.installTargetItemTitle}>
                {t['com.streamlain.marketplace.import.target.new']()}
              </span>
            </button>
            {isCheckingDocs ? (
              <div className={styles.installTargetEmpty}>
                {t['com.streamlain.marketplace.import.target.loading']()}
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <button
                  key={option.id}
                  type="button"
                  className={styles.installTargetItem}
                  data-selected={option.id === targetDocId || undefined}
                  onClick={() => {
                    onTargetDocChange(option.id);
                    setOpen(false);
                  }}
                >
                  <span className={styles.installTargetItemTitle}>
                    {option.title ||
                      t['com.streamlain.marketplace.import.target.untitled']()}
                  </span>
                  {option.sizeBytes !== null ? (
                    <span className={styles.installTargetItemMeta}>
                      {formatDocumentSizeScore(option.sizeBytes)}
                    </span>
                  ) : null}
                </button>
              ))
            ) : (
              <div className={styles.installTargetEmpty}>
                {query
                  ? t['com.streamlain.marketplace.import.target.no-results']()
                  : t['com.streamlain.marketplace.import.target.empty']()}
              </div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function isBoundaryStepDependency(dep: TemplateDependencyInfo): boolean {
  if (dep.templateType !== 'step') return false;
  if (dep.stepInsertType === 'start' || dep.stepInsertType === 'end') {
    return true;
  }
  const normalized = dep.title.trim().toLowerCase();
  return (
    normalized === 'start' || normalized === 'end' || normalized === 'ende'
  );
}

function visibleDependencies(
  dependencies: readonly TemplateDependencyInfo[]
): TemplateDependencyInfo[] {
  return dependencies.filter(dep => !isBoundaryStepDependency(dep));
}

function DependencyPreviewList({
  dependencies,
  workspaceId,
}: {
  dependencies: readonly TemplateDependencyInfo[];
  workspaceId: string;
}) {
  const t = useI18n();
  const displayDependencies = visibleDependencies(dependencies);
  const grouped = displayDependencies.reduce<{
    workflows: TemplateDependencyInfo[];
    automations: TemplateDependencyInfo[];
    steps: TemplateDependencyInfo[];
    appBlocks: TemplateDependencyInfo[];
    other: TemplateDependencyInfo[];
  }>(
    (acc, dep) => {
      switch (dep.templateType) {
        case 'workflow':
          acc.workflows.push(dep);
          break;
        case 'automation':
          acc.automations.push(dep);
          break;
        case 'step':
          acc.steps.push(dep);
          break;
        case 'app_block':
          acc.appBlocks.push(dep);
          break;
        default:
          acc.other.push(dep);
      }
      return acc;
    },
    { workflows: [], automations: [], steps: [], appBlocks: [], other: [] }
  );

  const renderItems = (items: readonly TemplateDependencyInfo[]) => (
    <PreviewList
      items={items.map(dep => ({
        id: dep.templateId,
        title: dep.title,
        meta: `${templateTypeLabel(dep.templateType, t)} · ${
          dep.semanticVersion
        }`,
        badge: (
          <span
            className={
              dep.status === 'missing' || dep.status === 'version_conflict'
                ? styles.importListBadgeWarning
                : styles.importListBadgeSuccess
            }
          >
            {dep.status === 'missing'
              ? t['com.streamlain.marketplace.import.dependency.missing']()
              : dep.status === 'version_conflict'
                ? marketplaceCopy(
                    t,
                    'com.streamlain.marketplace.import.dependency.version-conflict',
                    'Andere Version installiert'
                  )
                : dep.status === 'preinstalled'
                  ? t[
                      'com.streamlain.marketplace.import.dependency.preinstalled'
                    ]()
                  : t[
                      'com.streamlain.marketplace.import.dependency.installed'
                    ]()}
          </span>
        ),
        action:
          dep.status === 'missing' || dep.status === 'version_conflict' ? (
            <button
              type="button"
              className={styles.importListAction}
              onClick={() =>
                openMarketplaceTemplateInNewTab({
                  workspaceId,
                  templateId: dep.templateId,
                  templateType: dep.templateType,
                })
              }
            >
              {dep.status === 'version_conflict'
                ? marketplaceCopy(
                    t,
                    'com.streamlain.marketplace.import.dependency.open-update',
                    'Update öffnen'
                  )
                : marketplaceCopy(
                    t,
                    'com.streamlain.marketplace.import.dependency.open-marketplace',
                    'Im Marketplace öffnen'
                  )}
            </button>
          ) : undefined,
      }))}
      empty={t['com.streamlain.marketplace.import.card.dependencies.none']()}
    />
  );

  if (displayDependencies.length === 0) {
    return (
      <div className={styles.importEmptyLine}>
        {t['com.streamlain.marketplace.import.card.dependencies.none']()}
      </div>
    );
  }

  return (
    <div className={styles.importDependencyGroups}>
      {grouped.workflows.length > 0 && (
        <div className={styles.importDependencyGroup}>
          <div className={styles.importSubsectionTitle}>
            {t['com.streamlain.marketplace.import.dependencies.workflows']()}
          </div>
          {renderItems(grouped.workflows)}
        </div>
      )}
      {grouped.automations.length > 0 && (
        <div className={styles.importDependencyGroup}>
          <div className={styles.importSubsectionTitle}>
            {t['com.streamlain.marketplace.import.dependencies.automations']()}
          </div>
          {renderItems(grouped.automations)}
        </div>
      )}
      {grouped.steps.length > 0 && (
        <div className={styles.importDependencyGroup}>
          <div className={styles.importSubsectionTitle}>
            {t['com.streamlain.marketplace.import.dependencies.steps']()}
          </div>
          {renderItems(grouped.steps)}
        </div>
      )}
      {grouped.appBlocks.length > 0 && (
        <div className={styles.importDependencyGroup}>
          <div className={styles.importSubsectionTitle}>
            {t['com.streamlain.marketplace.import.dependencies.appBlocks']()}
          </div>
          {renderItems(grouped.appBlocks)}
        </div>
      )}
      {grouped.other.length > 0 && (
        <div className={styles.importDependencyGroup}>
          <div className={styles.importSubsectionTitle}>
            {t['com.streamlain.marketplace.import.dependencies.other']()}
          </div>
          {renderItems(grouped.other)}
        </div>
      )}
    </div>
  );
}

function buildDatabaseColumnGroups(
  columns: readonly ColumnPreview[],
  t: MarketplaceI18n
): DatabaseColumnGroup[] {
  const groups = new Map<string, DatabaseColumnGroup>();

  columns.forEach((column, index) => {
    const key = column.databaseKey ?? 'database.unknown';
    const existing = groups.get(key);
    const label = databaseSurfaceLabel(column, t);
    if (existing) {
      existing.columns.push(column);
      return;
    }
    groups.set(key, {
      key: `${key}:${index}`,
      label,
      role: column.databaseRole,
      columns: [column],
    });
  });

  return Array.from(groups.values());
}

function databaseSurfaceLabel(
  column: ColumnPreview,
  t: MarketplaceI18n
): string {
  if (column.databaseLabel) return column.databaseLabel;
  if (column.databaseRole === 'input') {
    return t['com.streamlain.marketplace.import.database.input']();
  }
  if (column.databaseRole === 'output') {
    return typeof column.databaseIndex === 'number' && column.databaseIndex > 0
      ? t['com.streamlain.marketplace.import.database.output-numbered']({
          number: String(column.databaseIndex + 1),
        })
      : t['com.streamlain.marketplace.import.database.output']();
  }
  return (
    column.databaseLabel ??
    t['com.streamlain.marketplace.import.database.unknown']()
  );
}

function databaseRoleLabel(
  role: ColumnPreview['databaseRole'] | undefined,
  t: MarketplaceI18n
): string {
  if (role === 'input') {
    return t['com.streamlain.marketplace.import.database.role.input']();
  }
  if (role === 'output') {
    return t['com.streamlain.marketplace.import.database.role.output']();
  }
  return t['com.streamlain.marketplace.import.database.role.database']();
}

function DatabaseColumnPreview({
  columns,
}: {
  columns: readonly ColumnPreview[];
}) {
  const t = useI18n();
  const groups = buildDatabaseColumnGroups(columns, t);
  const inputGroups = groups.filter(group => group.role === 'input');
  const outputGroups = groups.filter(group => group.role === 'output');
  const otherGroups = groups.filter(
    group => group.role !== 'input' && group.role !== 'output'
  );
  if (groups.length === 0) {
    return (
      <div className={styles.importEmptyLine}>
        {t['com.streamlain.marketplace.import.card.databases.none']()}
      </div>
    );
  }

  const renderGroup = (group: DatabaseColumnGroup) => {
    const previewColumns = group.columns.slice(0, 12);
    const remainingColumnCount = Math.max(
      0,
      group.columns.length - previewColumns.length
    );
    return (
      <div key={group.key} className={styles.importDatabaseGroup}>
        <div className={styles.importDatabaseHeader}>
          <div>
            <div className={styles.importDatabaseTitle}>{group.label}</div>
            <div className={styles.importDatabaseMeta}>
              {databaseRoleLabel(group.role, t)} ·{' '}
              {t['com.streamlain.marketplace.import.database.columns']({
                count: String(group.columns.length),
              })}
            </div>
          </div>
        </div>
        <div className={styles.importColumnGrid}>
          {previewColumns.map(column => (
            <span
              key={column.id}
              className={styles.importColumnPill}
              title={column.redacted ? column.type : column.name}
            >
              <span>{column.name}</span>
              <span className={styles.importColumnType}>{column.type}</span>
            </span>
          ))}
          {remainingColumnCount > 0 && (
            <span className={styles.importColumnPill}>
              {t['com.streamlain.marketplace.import.columns.more']({
                count: String(remainingColumnCount),
              })}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderLane = (
    role: 'input' | 'output',
    laneGroups: readonly DatabaseColumnGroup[]
  ) => (
    <section className={styles.importDatabaseLane}>
      <div className={styles.importDatabaseLaneHeader}>
        {role === 'input'
          ? t['com.streamlain.marketplace.import.database.input-lane']()
          : t['com.streamlain.marketplace.import.database.output-lane']()}
      </div>
      {laneGroups.length > 0 ? (
        <div className={styles.importDatabaseGroups}>
          {laneGroups.map(renderGroup)}
        </div>
      ) : (
        <div className={styles.importEmptyLine}>
          {role === 'input'
            ? t['com.streamlain.marketplace.import.database.input.none']()
            : t['com.streamlain.marketplace.import.database.output.none']()}
        </div>
      )}
    </section>
  );

  return (
    <>
      <div className={styles.importDatabaseLanes}>
        {renderLane('input', inputGroups)}
        {renderLane('output', outputGroups)}
      </div>
      {otherGroups.length > 0 && (
        <div className={styles.importDatabaseGroups}>
          {otherGroups.map(renderGroup)}
        </div>
      )}
    </>
  );
}

interface DatabaseBindingSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

function DatabaseBindingMenuSelect({
  value,
  placeholder,
  options,
  disabled,
  onChange,
}: {
  value: string;
  placeholder: string;
  options: readonly DatabaseBindingSelectOption[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const current = options.find(option => option.value === value);
  const menuItems =
    options.length > 0
      ? options.map(option => (
          <MenuItem
            key={option.value || '__empty'}
            disabled={option.disabled}
            onSelect={() => {
              if (!option.disabled) onChange(option.value);
            }}
            data-selected={option.value === value || undefined}
          >
            {option.label}
          </MenuItem>
        ))
      : [
          <MenuItem key="__empty" disabled>
            {placeholder}
          </MenuItem>,
        ];

  return (
    <Menu contentOptions={{ align: 'start' }} items={menuItems}>
      <MenuTrigger
        block
        className={styles.databaseBindingSelect}
        disabled={disabled}
        contentStyle={{
          width: 0,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {current?.label ?? placeholder}
      </MenuTrigger>
    </Menu>
  );
}

function AutomationDatabaseBindingPanel({
  bindings,
  targetDocId,
  disabled,
  onChange,
}: {
  bindings: AutomationDatabaseBindings | undefined;
  targetDocId: string | undefined;
  disabled: boolean;
  onChange: (bindings: AutomationDatabaseBindings) => void;
}) {
  const t = useI18n();
  const discoveryService = useService(DatabaseDiscoveryService);
  const databasesByDoc = useLiveData(discoveryService.writableDatabasesByDoc$);
  const [autoMatchSurfaceKey, setAutoMatchSurfaceKey] = useState<string | null>(
    null
  );

  const targetDatabases = useMemo(() => {
    if (!targetDocId) return [];
    return (
      databasesByDoc.find(group => group.docId === targetDocId)?.databases ?? []
    );
  }, [databasesByDoc, targetDocId]);

  const selectedSurface = autoMatchSurfaceKey
    ? bindings?.surfaces.find(
        surface => surface.databaseKey === autoMatchSurfaceKey
      )
    : undefined;
  const selectedDatabase = selectedSurface?.targetDatabaseBlockId
    ? targetDatabases.find(
        database => database.blockId === selectedSurface.targetDatabaseBlockId
      )
    : undefined;

  const getDatabaseColumnDefaultAction = useCallback(
    (database: DatabaseInfo | undefined) =>
      database?.schemaLocked ? 'map_existing' : 'create_column',
    []
  );

  const updateSurface = useCallback(
    (
      surfaceKey: string,
      updater: (
        surface: AutomationDatabaseSurfaceBinding
      ) => AutomationDatabaseSurfaceBinding
    ) => {
      if (!bindings) return;
      onChange({
        ...bindings,
        surfaces: bindings.surfaces.map(surface =>
          surface.databaseKey === surfaceKey ? updater(surface) : surface
        ),
      });
    },
    [bindings, onChange]
  );

  const handleModeChange = useCallback(
    (
      surface: AutomationDatabaseSurfaceBinding,
      mode: AutomationDatabaseSurfaceBinding['mode']
    ) => {
      updateSurface(surface.databaseKey, current => ({
        ...current,
        mode,
        ...(mode === 'create_new'
          ? {
              targetDatabaseBlockId: undefined,
              targetDatabaseName: undefined,
              columns: current.columns.map(column => ({
                ...column,
                action: 'create_column' as const,
                targetColumnId: undefined,
                targetColumnName: undefined,
                targetColumnType: undefined,
              })),
            }
          : { targetDocId }),
      }));
    },
    [targetDocId, updateSurface]
  );

  const handleDatabaseChange = useCallback(
    (surface: AutomationDatabaseSurfaceBinding, databaseId: string) => {
      const database = targetDatabases.find(db => db.blockId === databaseId);
      const action = getDatabaseColumnDefaultAction(database);
      updateSurface(surface.databaseKey, current => ({
        ...current,
        mode: 'use_existing',
        targetDocId,
        targetDatabaseBlockId: database?.blockId,
        targetDatabaseName: database?.databaseTitle,
        columns: current.columns.map(column => ({
          ...column,
          action,
          targetColumnId: undefined,
          targetColumnName: undefined,
          targetColumnType: undefined,
        })),
      }));
    },
    [
      getDatabaseColumnDefaultAction,
      targetDatabases,
      targetDocId,
      updateSurface,
    ]
  );

  const handleColumnChange = useCallback(
    (
      surface: AutomationDatabaseSurfaceBinding,
      column: AutomationDatabaseColumnBinding,
      targetColumnId: string
    ) => {
      const database = targetDatabases.find(
        db => db.blockId === surface.targetDatabaseBlockId
      );
      const targetColumn = database?.columns.find(
        candidate => candidate.id === targetColumnId
      );
      updateSurface(surface.databaseKey, current => ({
        ...current,
        columns: current.columns.map(currentColumn => {
          if (currentColumn.templateColumnId !== column.templateColumnId) {
            return currentColumn;
          }
          if (!targetColumn) {
            return {
              ...currentColumn,
              action: 'create_column',
              targetColumnId: undefined,
              targetColumnName: undefined,
              targetColumnType: undefined,
            };
          }
          return {
            ...currentColumn,
            action: 'map_existing',
            targetColumnId: targetColumn.id,
            targetColumnName: targetColumn.name,
            targetColumnType: targetColumn.type,
          };
        }),
      }));
    },
    [targetDatabases, updateSurface]
  );

  const handleAutoMatchApply = useCallback(
    (suggestions: AutoMatchSuggestion[]) => {
      if (!selectedSurface || !selectedDatabase) return;
      const suggestionsByColumn = new Map(
        suggestions.map(suggestion => [suggestion.targetColumnId, suggestion])
      );
      updateSurface(selectedSurface.databaseKey, current => ({
        ...current,
        columns: current.columns.map(column => {
          const suggestion = suggestionsByColumn.get(column.templateColumnId);
          const targetColumn = suggestion
            ? selectedDatabase.columns.find(
                databaseColumn => databaseColumn.id === suggestion.responseField
              )
            : undefined;
          if (!targetColumn) return column;
          return {
            ...column,
            action: 'map_existing',
            targetColumnId: targetColumn.id,
            targetColumnName: targetColumn.name,
            targetColumnType: targetColumn.type,
          };
        }),
      }));
    },
    [selectedDatabase, selectedSurface, updateSurface]
  );

  if (!bindings || bindings.surfaces.length === 0) return null;

  if (!targetDocId) {
    return (
      <section className={styles.databaseBindingPanel}>
        <div className={styles.databaseBindingHeader}>
          <div>
            <div className={styles.databaseBindingTitle}>
              {marketplaceCopy(
                t,
                'com.streamlain.marketplace.import.database-binding.title',
                'Datenbanken'
              )}
            </div>
            <div className={styles.databaseBindingText}>
              {marketplaceCopy(
                t,
                'com.streamlain.marketplace.import.database-binding.new-doc',
                'Für ein neues Dokument werden die Datenbanken aus der Vorlage angelegt.'
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.databaseBindingPanel}>
      {autoMatchSurfaceKey && selectedSurface && selectedDatabase ? (
        <AutoMatchDialog
          open={true}
          onClose={() => setAutoMatchSurfaceKey(null)}
          sourceFields={selectedDatabase.columns.map(column => ({
            path: column.id,
            displayName: column.name,
            type: column.type,
          }))}
          targetFields={selectedSurface.columns.map(column => ({
            id: column.templateColumnId,
            name: column.templateColumnName,
            type: column.templateColumnType,
          }))}
          existingTargetIds={
            new Set(
              selectedSurface.columns
                .filter(column => column.action === 'map_existing')
                .map(column => column.templateColumnId)
            )
          }
          sourceLabel={marketplaceCopy(
            t,
            'com.streamlain.marketplace.import.database-binding.existing-column',
            'Bestehende Spalte'
          )}
          targetLabel={marketplaceCopy(
            t,
            'com.streamlain.marketplace.import.database-binding.template-column',
            'Vorgesehene Spalte'
          )}
          onApply={handleAutoMatchApply}
        />
      ) : null}
      <div className={styles.databaseBindingHeader}>
        <div>
          <div className={styles.databaseBindingTitle}>
            {marketplaceCopy(
              t,
              'com.streamlain.marketplace.import.database-binding.title',
              'Datenbanken'
            )}
          </div>
          <div className={styles.databaseBindingText}>
            {marketplaceCopy(
              t,
              'com.streamlain.marketplace.import.database-binding.description',
              'Lege neue Datenbanken an oder nutze vorhandene Datenbanken im ausgewählten Dokument.'
            )}
          </div>
        </div>
      </div>
      <div className={styles.databaseBindingSurfaceList}>
        {bindings.surfaces.map(surface => {
          const database = surface.targetDatabaseBlockId
            ? targetDatabases.find(
                candidate => candidate.blockId === surface.targetDatabaseBlockId
              )
            : undefined;
          return (
            <section
              key={surface.databaseKey}
              className={styles.databaseBindingSurface}
            >
              <div className={styles.databaseBindingSurfaceHeader}>
                <div>
                  <div className={styles.databaseBindingSurfaceTitle}>
                    {surface.databaseLabel}
                  </div>
                  <div className={styles.databaseBindingText}>
                    {databaseRoleLabel(surface.databaseRole, t)} ·{' '}
                    {t['com.streamlain.marketplace.import.database.columns']({
                      count: String(surface.columns.length),
                    })}
                  </div>
                </div>
                <div className={styles.databaseBindingModeGroup}>
                  <button
                    type="button"
                    className={styles.databaseBindingModeButton}
                    data-selected={surface.mode === 'create_new' || undefined}
                    disabled={disabled}
                    onClick={() => handleModeChange(surface, 'create_new')}
                  >
                    {marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.database-binding.mode-new',
                      'Neu'
                    )}
                  </button>
                  <button
                    type="button"
                    className={styles.databaseBindingModeButton}
                    data-selected={surface.mode === 'use_existing' || undefined}
                    disabled={disabled || targetDatabases.length === 0}
                    onClick={() => handleModeChange(surface, 'use_existing')}
                  >
                    {marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.database-binding.mode-existing',
                      'Bestehend'
                    )}
                  </button>
                </div>
              </div>
              {surface.mode === 'use_existing' ? (
                <>
                  <div className={styles.databaseBindingControls}>
                    <DatabaseBindingMenuSelect
                      value={surface.targetDatabaseBlockId ?? ''}
                      disabled={disabled}
                      placeholder={marketplaceCopy(
                        t,
                        'com.streamlain.marketplace.import.database-binding.select-database',
                        'Datenbank wählen'
                      )}
                      options={[
                        {
                          value: '',
                          label: marketplaceCopy(
                            t,
                            'com.streamlain.marketplace.import.database-binding.select-database',
                            'Datenbank wählen'
                          ),
                        },
                        ...targetDatabases.map(databaseOption => ({
                          value: databaseOption.blockId,
                          label: databaseOption.databaseTitle,
                        })),
                      ]}
                      onChange={databaseId =>
                        handleDatabaseChange(surface, databaseId)
                      }
                    />
                    <button
                      type="button"
                      className={styles.databaseBindingAutoMapButton}
                      disabled={disabled || !database}
                      onClick={() =>
                        setAutoMatchSurfaceKey(surface.databaseKey)
                      }
                    >
                      {t['com.streamlain.streamlain.automap.button']()}
                    </button>
                  </div>
                  {database ? (
                    <>
                      {database.schemaLocked ? (
                        <div className={styles.databaseBindingText}>
                          {marketplaceCopy(
                            t,
                            'com.streamlain.marketplace.import.database-binding.schema-locked',
                            'Dieses Datenbankschema ist gesperrt. Ordne jede benötigte Spalte einer bestehenden Spalte zu.'
                          )}
                        </div>
                      ) : null}
                      <div className={styles.databaseBindingColumnRows}>
                        {surface.columns.map(column => {
                          const createColumnLabel = database.schemaLocked
                            ? marketplaceCopy(
                                t,
                                'com.streamlain.marketplace.import.database-binding.select-existing-column',
                                'Bestehende Spalte wählen'
                              )
                            : marketplaceCopy(
                                t,
                                'com.streamlain.marketplace.import.database-binding.create-column',
                                'Neue Spalte anlegen'
                              );
                          return (
                            <div
                              key={column.templateColumnId}
                              className={styles.databaseBindingColumnRow}
                            >
                              <div
                                className={styles.databaseBindingColumnExpected}
                              >
                                <span>{column.templateColumnName}</span>
                                <span
                                  className={styles.databaseBindingTypePill}
                                >
                                  {column.templateColumnType}
                                </span>
                              </div>
                              <DatabaseBindingMenuSelect
                                value={column.targetColumnId ?? ''}
                                disabled={disabled}
                                placeholder={createColumnLabel}
                                options={[
                                  {
                                    value: '',
                                    label: createColumnLabel,
                                    disabled: database.schemaLocked,
                                  },
                                  ...database.columns.map(targetColumn => ({
                                    value: targetColumn.id,
                                    label: `${targetColumn.name} · ${targetColumn.type}`,
                                  })),
                                ]}
                                onChange={targetColumnId =>
                                  handleColumnChange(
                                    surface,
                                    column,
                                    targetColumnId
                                  )
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className={styles.installTargetEmpty}>
                      {targetDatabases.length === 0
                        ? marketplaceCopy(
                            t,
                            'com.streamlain.marketplace.import.database-binding.no-databases',
                            'Dieses Dokument enthält noch keine beschreibbare Datenbank.'
                          )
                        : marketplaceCopy(
                            t,
                            'com.streamlain.marketplace.import.database-binding.select-first',
                            'Wähle eine Datenbank, um Spalten zuzuordnen.'
                          )}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.databaseBindingText}>
                  {marketplaceCopy(
                    t,
                    'com.streamlain.marketplace.import.database-binding.new-surface',
                    'Diese Datenbank wird aus der Vorlage angelegt.'
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </section>
  );
}

function SlotsPreviewBody({
  slots,
  picker,
}: {
  slots: NonNullable<TemplatePrerequisites['slotsPreview']>;
  picker: ReturnType<typeof useLocalePicker> | null;
}) {
  const t = useI18n();
  const language = picker?.language ?? 'en';
  const groups: Array<{
    key: 'inputs' | 'outputs';
    label: string;
    items: typeof slots.inputs;
  }> = [];
  if (slots.inputs.length > 0) {
    groups.push({
      key: 'inputs',
      label: t['com.streamlain.marketplace.import.card.slots.inputs'](),
      items: slots.inputs,
    });
  }
  if (slots.outputs.length > 0) {
    groups.push({
      key: 'outputs',
      label: t['com.streamlain.marketplace.import.card.slots.outputs'](),
      items: slots.outputs,
    });
  }
  return (
    <div className={styles.importDependencyGroups}>
      {groups.map(group => (
        <div key={group.key} className={styles.importDependencyGroup}>
          <div className={styles.importSubsectionTitle}>{group.label}</div>
          <div className={styles.slotList}>
            {group.items.map(slot => {
              const label =
                slot.labelsI18n?.[language] ??
                slot.labelsI18n?.['en'] ??
                slot.name;
              const description =
                slot.descriptionsI18n?.[language] ??
                slot.descriptionsI18n?.['en'] ??
                slot.description;
              return (
                <div key={slot.name} className={styles.slotRow}>
                  <div className={styles.slotRowMain}>
                    <span className={styles.slotLabel}>{label}</span>
                    <span className={styles.slotTypePill}>{slot.type}</span>
                    {slot.required ? (
                      <span className={styles.slotRequiredPill}>
                        {t[
                          'com.streamlain.marketplace.import.card.slots.required'
                        ]()}
                      </span>
                    ) : null}
                  </div>
                  {description ? (
                    <p className={styles.slotDescription}>{description}</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ConfigPreviewList({ items }: { items: readonly ConfigPreviewItem[] }) {
  const t = useI18n();
  const picker = useLocalePicker();
  const grouped = items.reduce<Map<string, ConfigPreviewItem[]>>(
    (acc, item) => {
      const key = item.stepName ?? 'template';
      const list = acc.get(key) ?? [];
      list.push(item);
      acc.set(key, list);
      return acc;
    },
    new Map()
  );

  return (
    <div className={styles.importDependencyGroups}>
      {Array.from(grouped.entries()).map(([groupName, groupItems]) => (
        <div key={groupName} className={styles.importDependencyGroup}>
          <div className={styles.importSubsectionTitle}>{groupName}</div>
          <PreviewList
            items={groupItems.map(item => ({
              id: item.id,
              title: configPreviewLabel(item, picker),
              meta: item.key,
              ...configPreviewValuePlacement(item),
            }))}
            empty={t['com.streamlain.marketplace.import.settings.none']()}
          />
        </div>
      ))}
    </div>
  );
}

function configPreviewValuePlacement(
  item: ConfigPreviewItem
): Pick<PreviewListItem, 'badge' | 'details'> {
  const value = <ConfigPreviewValue configKey={item.key} value={item.value} />;
  return isExpandableConfigValue(item.key, item.value)
    ? { details: value }
    : { badge: value };
}

function ConfigPreviewValue({
  configKey,
  value,
}: {
  configKey: string;
  value: string;
}) {
  const t = useI18n();
  const formattedValue = formatConfigValue(configKey, value);
  const label = t['com.streamlain.marketplace.import.settings.default']({
    value: formattedValue,
  });

  if (!isExpandableConfigValue(configKey, value)) {
    return <span className={styles.importListBadgeNeutral}>{label}</span>;
  }

  return (
    <details className={styles.importConfigValue}>
      <summary className={styles.importConfigValueSummary}>
        <span className={styles.importConfigValueLabel}>{label}</span>
        <span className={styles.importConfigValueChevron} aria-hidden>
          {'\u203A'}
        </span>
      </summary>
      <pre className={styles.importConfigValueBody}>{formattedValue}</pre>
    </details>
  );
}

function isExpandableConfigValue(configKey: string, value: string): boolean {
  const formattedValue = formatConfigValue(configKey, value);
  return formattedValue.length > 48 || formattedValue.includes('\n');
}

function configPreviewLabel(
  item: ConfigPreviewItem,
  picker: ReturnType<typeof useLocalePicker> | null
): string {
  const picked = picker?.pickTitle(item.labelsI18n);
  return picked || item.label || friendlyConfigKey(item.key, picker?.language);
}

function friendlyConfigKey(key: string, language?: string): string {
  const de = language?.toLowerCase().startsWith('de');
  const labels: Record<string, { en: string; de: string }> = {
    bitrate: { en: 'Bitrate', de: 'Bitrate' },
    codec: { en: 'Audio codec', de: 'Audio-Codec' },
    channels: { en: 'Channels', de: 'Kanäle' },
    sampleRate: { en: 'Sample rate', de: 'Abtastrate' },
    startTime: { en: 'Start time', de: 'Startzeit' },
    endTime: { en: 'End time', de: 'Endzeit' },
  };
  const mapped = labels[key];
  if (mapped) return de ? mapped.de : mapped.en;
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function formatConfigValue(key: string, value: string): string {
  if (key === 'bitrate' && /^\d+k$/i.test(value)) {
    return `${value.slice(0, -1)} kbps`;
  }
  if (key === 'sampleRate' && /^\d+$/.test(value)) {
    const hz = Number(value);
    return hz >= 1000
      ? `${(hz / 1000).toLocaleString('de-DE', {
          maximumFractionDigits: 2,
        })} kHz`
      : `${value} Hz`;
  }
  if (key === 'channels') {
    if (value === '1') return 'Mono';
    if (value === '2') return 'Stereo';
  }
  if (key === 'codec') {
    const codecs: Record<string, string> = {
      libmp3lame: 'MP3',
      aac: 'AAC',
      libopus: 'Opus',
    };
    return codecs[value] ?? value;
  }
  return value;
}

function StepPreview({
  template,
  workspaceId,
  targetVersionNumber,
  prerequisites,
  isLoading,
  error,
  showCommunityMetrics = true,
  onRatingAction,
  headerExtra,
  extraCards,
  containedScroll = true,
}: {
  template: MarketplaceTemplate;
  workspaceId: string;
  targetVersionNumber?: number;
  prerequisites: TemplatePrerequisites | null;
  isLoading: boolean;
  error: string | null;
  showCommunityMetrics?: boolean;
  onRatingAction?: () => void;
  headerExtra?: ReactNode;
  extraCards?: ReactNode;
  containedScroll?: boolean;
}) {
  const t = useI18n();
  // sl-k94y.5.1: useI18n() retained for SUPPORTED_LANGUAGES + t-fn elsewhere;
  // useLocalePicker subscribes to LocaleSettings for the locale-aware chain.
  const picker = useLocalePicker();
  if (error) {
    return (
      <div style={{ padding: '20px', flex: 1 }}>
        <div className={styles.credentialWarning}>
          {'\u26A0'}{' '}
          {t['com.streamlain.marketplace.import.error.prerequisites']({
            error,
          })}
        </div>
      </div>
    );
  }

  if (isLoading || !prerequisites) {
    return (
      <div style={{ padding: '20px', flex: 1 }}>
        <div className={styles.stateContainer}>
          <div className={styles.spinner} />
          {t['com.streamlain.marketplace.import.loading']()}
        </div>
      </div>
    );
  }

  const externalCreds = prerequisites.credentials.filter(
    c => !c.isProvidedByStreamlain
  );
  const autoMappedCreds = prerequisites.credentials.filter(
    c => c.isProvidedByStreamlain
  );
  const unknownCreds = prerequisites.credentials.filter(
    c => c.type === 'unknown'
  );
  const description = picker?.pickDescription(template.descriptionsI18n) ?? '';
  const typeStyle = TYPE_CONFIG[template.templateType] ?? TYPE_CONFIG.workflow;
  const TypeGlyph = typeStyle.Glyph;
  const tintVar = {
    ['--card-tint' as string]: typeStyle.tint,
    ...(containedScroll
      ? {}
      : {
          flex: 'none',
          minHeight: 'auto',
          overflow: 'visible',
        }),
  } as CSSProperties;
  const downloads = template.downloadCount ?? 0;
  const rating = template.averageRating ?? 0;
  const executionCount = template.executionCount ?? 0;
  const dependencies = prerequisites.dependencies?.dependencies ?? [];
  const visibleDependencyList = visibleDependencies(dependencies);
  const missingDependencies = visibleDependencyList.filter(
    dep => dep.status === 'missing'
  );
  const configPreview = prerequisites.configPreview ?? [];
  const showDatabasePreview = template.templateType === 'automation';
  const categoryLabel = getMarketplaceCategoryLabel(t, template.category);
  const previewBodyClassName = [
    styles.importPreviewBody,
    containedScroll ? null : styles.importPreviewBodyPage,
  ]
    .filter(Boolean)
    .join(' ');
  const communityMetrics = showCommunityMetrics ? (
    <div className={styles.detailMetricsRow}>
      {onRatingAction ? (
        <button
          type="button"
          className={styles.detailMetricsPill}
          onClick={onRatingAction}
        >
          <span className={styles.detailMetric}>
            <DownloadIcon />
            <span className={styles.detailMetricValue}>
              {formatCount(downloads)}
            </span>
            <span className={styles.detailMetricLabel}>
              {t['com.streamlain.marketplace.browse.detail.metric.downloads']()}
            </span>
          </span>
          <span className={styles.detailMetricDivider} aria-hidden />
          <span className={styles.detailMetric}>
            <StarIcon />
            <span className={styles.detailMetricValue}>
              {formatRating(rating)}
            </span>
            <span className={styles.detailMetricLabel}>
              {t['com.streamlain.marketplace.browse.detail.metric.rating']()}
            </span>
          </span>
        </button>
      ) : (
        <span className={styles.detailMetricsPillPassive}>
          <span className={styles.detailMetric}>
            <DownloadIcon />
            <span className={styles.detailMetricValue}>
              {formatCount(downloads)}
            </span>
            <span className={styles.detailMetricLabel}>
              {t['com.streamlain.marketplace.browse.detail.metric.downloads']()}
            </span>
          </span>
          <span className={styles.detailMetricDivider} aria-hidden />
          <span className={styles.detailMetric}>
            <StarIcon />
            <span className={styles.detailMetricValue}>
              {formatRating(rating)}
            </span>
            <span className={styles.detailMetricLabel}>
              {t['com.streamlain.marketplace.browse.detail.metric.rating']()}
            </span>
          </span>
        </span>
      )}
      {executionCount > 0 && template.templateType !== 'step' && (
        <span className={styles.detailMetric}>
          <PlayIcon />
          <span className={styles.detailMetricValue}>
            {formatCount(executionCount)}
          </span>
          <span className={styles.detailMetricLabel}>
            {t['com.streamlain.marketplace.browse.detail.metric.runs']()}
          </span>
        </span>
      )}
    </div>
  ) : null;

  return (
    <div className={styles.importPreviewContent} style={tintVar}>
      {containedScroll ? (
        <section className={styles.importHero}>
          <span className={styles.importHeroIcon}>
            <TypeGlyph size={28} />
          </span>
          <div className={styles.importHeroBody}>
            <h2 className={styles.importHeroTitle}>
              {picker?.pickTitle(template.titlesI18n) ?? ''}
            </h2>
            <div className={styles.importHeroMeta}>
              {headerExtra ? (
                <>
                  {headerExtra}
                  <span aria-hidden>·</span>
                </>
              ) : null}
              <span>
                {t[
                  `com.streamlain.marketplace.card.type.${template.templateType}` as keyof typeof t
                ]?.() ?? template.templateType}
              </span>
              <span aria-hidden>·</span>
              <span>{categoryLabel}</span>
            </div>
            {communityMetrics}
            {description && (
              <div className={styles.importHeroAbout}>
                <h3 className={styles.detailSectionTitle}>
                  {t[
                    'com.streamlain.marketplace.browse.detail.section.about'
                  ]()}
                </h3>
                <p className={styles.importHeroDescription}>{description}</p>
              </div>
            )}
          </div>
        </section>
      ) : null}

      <div className={previewBodyClassName}>
        {template.templateType === 'doc' ||
        template.templateType === 'app' ||
        template.templateType === 'app_block' ? (
          <DocTemplatePreviewPanel
            template={template}
            targetVersionNumber={targetVersionNumber}
            workspaceId={workspaceId}
          />
        ) : null}

        <div
          className={clsx(
            styles.importPreviewMainGrid,
            configPreview.length === 0 && styles.importPreviewMainGridSingle
          )}
        >
          <div className={styles.importPreviewLeftColumn}>
            {/* Step templates are atomic — they never depend on other
                marketplace templates, so the dependency card is pure noise
                in that mode. Workflow/automation/doc keep the card. */}
            {template.templateType === 'step' &&
            visibleDependencyList.length === 0 ? null : (
              <PreviewImpactCard
                title={t[
                  'com.streamlain.marketplace.import.card.dependencies.title'
                ]()}
                defaultOpen={visibleDependencyList.length <= 6}
                subtitle={
                  visibleDependencyList.length === 0
                    ? t[
                        'com.streamlain.marketplace.import.card.dependencies.none'
                      ]()
                    : t[
                        'com.streamlain.marketplace.import.card.dependencies.summary'
                      ]({
                        missing: String(missingDependencies.length),
                        total: String(visibleDependencyList.length),
                      })
                }
              >
                <DependencyPreviewList
                  dependencies={dependencies}
                  workspaceId={workspaceId}
                />
              </PreviewImpactCard>
            )}

            <PreviewImpactCard
              title={t['com.streamlain.marketplace.import.card.access.title']()}
              defaultOpen={externalCreds.length + autoMappedCreds.length <= 4}
              subtitle={
                externalCreds.length === 0
                  ? t['com.streamlain.marketplace.import.card.access.none']()
                  : t['com.streamlain.marketplace.import.card.access.external'](
                      {
                        count: String(externalCreds.length),
                      }
                    )
              }
            >
              <PreviewList
                items={[
                  ...externalCreds.map(cred => ({
                    id: cred.id,
                    title: cred.label,
                    meta: cred.type,
                    badge: cred.required ? (
                      <span
                        className={styles.importListBadgeWarning}
                        title={t[
                          'com.streamlain.marketplace.import.required'
                        ]()}
                      >
                        {t['com.streamlain.marketplace.import.required']()}
                      </span>
                    ) : undefined,
                  })),
                  ...autoMappedCreds.map(cred => ({
                    id: cred.id,
                    title: cred.label,
                    meta: cred.type,
                    badge: (
                      <span className={styles.importListBadgeSuccess}>
                        {t[
                          'com.streamlain.marketplace.import.provided-by-streamlain'
                        ]()}
                      </span>
                    ),
                  })),
                ]}
                empty={t[
                  'com.streamlain.marketplace.import.credentials.none'
                ]()}
              />
            </PreviewImpactCard>
          </div>

          {configPreview.length > 0 && (
            <div className={styles.importPreviewRightColumn}>
              <PreviewImpactCard
                title={t[
                  'com.streamlain.marketplace.import.card.settings.title'
                ]()}
                defaultOpen={configPreview.length <= 8}
                subtitle={t[
                  'com.streamlain.marketplace.import.card.settings.summary'
                ]({
                  count: String(configPreview.length),
                })}
              >
                <ConfigPreviewList items={configPreview} />
              </PreviewImpactCard>
            </div>
          )}
        </div>

        {prerequisites.compositePreflight ? (
          <PreviewImpactCard
            wide
            title={t[
              'com.streamlain.marketplace.import.card.composite.title'
            ]()}
            defaultOpen
            subtitle={t[
              'com.streamlain.marketplace.import.card.composite.summary'
            ]({
              count: String(prerequisites.compositePreflight.automationCount),
              maximum: String(prerequisites.compositePreflight.maxAutomations),
            })}
          >
            <PreviewList
              items={prerequisites.compositePreflight.revisions.map(item => ({
                id: item.slotKey,
                title: item.label,
                meta: t['com.streamlain.marketplace.import.card.composite.io']({
                  inputs: String(item.inputs.length),
                  outputs: String(item.outputs.length),
                }),
                badge: <span>{item.disposition}</span>,
              }))}
              empty={t[
                'com.streamlain.marketplace.import.card.composite.empty'
              ]()}
            />
            {prerequisites.compositePreflight.findings.map(finding => (
              <div
                key={`${finding.severity}:${finding.message}`}
                className={styles.credentialWarning}
                style={{ marginTop: '8px' }}
              >
                {finding.message ===
                'This template contains no embedded automations.'
                  ? t[
                      'com.streamlain.marketplace.import.card.composite.no-automations'
                    ]()
                  : finding.message}
              </div>
            ))}
            <div style={{ marginTop: '8px', fontSize: '12px' }}>
              {t['com.streamlain.marketplace.import.card.composite.technical']({
                state: prerequisites.compositePreflight.canSaveTechnicalRevision
                  ? t[
                      'com.streamlain.marketplace.import.card.composite.available'
                    ]()
                  : t[
                      'com.streamlain.marketplace.import.card.composite.read-only'
                    ](),
                promotion: prerequisites.compositePreflight.canPromoteListing
                  ? t[
                      'com.streamlain.marketplace.import.card.composite.available'
                    ]()
                  : t[
                      'com.streamlain.marketplace.import.card.composite.unavailable'
                    ](),
              })}
            </div>
          </PreviewImpactCard>
        ) : null}

        {prerequisites.slotsPreview &&
          (prerequisites.slotsPreview.inputs.length > 0 ||
            prerequisites.slotsPreview.outputs.length > 0) && (
            <PreviewImpactCard
              wide
              title={t['com.streamlain.marketplace.import.card.slots.title']()}
              subtitle={t[
                'com.streamlain.marketplace.import.card.slots.summary'
              ]({
                inputs: String(prerequisites.slotsPreview.inputs.length),
                outputs: String(prerequisites.slotsPreview.outputs.length),
              })}
            >
              <SlotsPreviewBody
                slots={prerequisites.slotsPreview}
                picker={picker}
              />
            </PreviewImpactCard>
          )}

        {showDatabasePreview && (
          <PreviewImpactCard
            wide
            title={t[
              'com.streamlain.marketplace.import.card.databases.title'
            ]()}
            defaultOpen={prerequisites.columnPreview.length <= 16}
            subtitle={
              prerequisites.columnPreview.length === 0
                ? t['com.streamlain.marketplace.import.card.databases.none']()
                : t['com.streamlain.marketplace.import.card.databases.summary'](
                    {
                      count: String(
                        new Set(
                          prerequisites.columnPreview.map(
                            c => c.databaseKey ?? 'database.unknown'
                          )
                        ).size
                      ),
                    }
                  )
            }
          >
            <DatabaseColumnPreview columns={prerequisites.columnPreview} />
          </PreviewImpactCard>
        )}

        {/* Surface template drift: credential ids referenced in templateData
          that the author forgot to declare in credentialRequirements. */}
        {unknownCreds.length > 0 && (
          <div
            className={styles.credentialWarning}
            style={{ marginTop: '8px' }}
          >
            {'\u26A0'} {unknownCreds.length} credential reference
            {unknownCreds.length !== 1 ? 's' : ''} in this template lack a
            declared type. You will need to pick a matching credential manually
            at the next step.
          </div>
        )}

        {/* sl-67kc.4.4: Nested-workflow references. Today the template
          catalog ships no workflow-invocation step-type, so this list
          is empty for every real template. When a future step-type
          lands the install boundary will reject the template (see
          template-install.service.ts#assertNestedWorkflowsInstalled);
          this block surfaces the same information up-front so the user
          is not surprised by a 422 at the very end. */}
        {prerequisites.nestedWorkflows.length > 0 && (
          <>
            <div
              className={styles.detailSectionTitle}
              style={{ marginTop: '12px' }}
            >
              Nested Workflows
            </div>
            <div className={styles.credentialWarning}>
              {'⚠'} This template references{' '}
              {prerequisites.nestedWorkflows.length} nested workflow
              {prerequisites.nestedWorkflows.length !== 1 ? 's' : ''}. Install
              will fail until workflow-invocation step-types are supported:
              <ul style={{ margin: '6px 0 0 18px', fontSize: '12px' }}>
                {prerequisites.nestedWorkflows.map(ref => (
                  <li key={ref.workflowId}>
                    <code>{ref.workflowId}</code>
                    {ref.referencedAtStepId ? (
                      <> (step {ref.referencedAtStepId})</>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {extraCards}
      </div>
    </div>
  );
}

interface ImportInstallResult {
  installedDocIds?: string[];
  workflowId?: string | null;
}

function installPermissionReasonText(
  requirement: TemplateInstallPermissionRequirement,
  t: MarketplaceI18n
): string {
  const key =
    `com.streamlain.marketplace.import.permission.${requirement.reason}` as keyof MarketplaceI18n;
  const candidate = t[key];
  return typeof candidate === 'function' ? candidate() : requirement.permission;
}

function InstallConfirmSection({
  title,
  subtitle,
  items,
  defaultOpen,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  items: readonly PreviewListItem[];
  defaultOpen?: boolean;
}) {
  const t = useI18n();
  const shouldOpen = defaultOpen ?? items.length <= 6;

  return (
    <details className={styles.installConfirmSection} open={shouldOpen}>
      <summary className={styles.installConfirmSummary}>
        <span>{title}</span>
        <span className={styles.installConfirmCount}>{items.length}</span>
      </summary>
      {subtitle ? (
        <div className={styles.installConfirmSubtitle}>{subtitle}</div>
      ) : null}
      <PreviewList
        items={items}
        empty={t['com.streamlain.marketplace.import.confirm.none']()}
      />
    </details>
  );
}

function InstallConfirmationDialog({
  template,
  targetVersionNumber,
  prerequisites,
  installStep,
  installResult,
  installError,
  installIssues,
  isInstalling,
  targetDocId,
  databaseBindings,
  appBlockTargetKind,
  appBlockTargetAppId,
  appBlockTargetTitle,
  appBlockTargetRootRoutePath,
  appBlockDatabaseMappingDecisions,
  appBlockPreflight,
  appBlockPreflightLoading,
  appBlockPreflightError,
  customApps,
  overlayPortal,
  onTargetDocChange,
  onDatabaseBindingsChange,
  onAppBlockTargetKindChange,
  onAppBlockTargetAppIdChange,
  onAppBlockTargetTitleChange,
  onAppBlockTargetRootRoutePathChange,
  onAppBlockDatabaseMappingDecisionsChange,
  onCancel,
  onConfirm,
  onOpen,
  onBackToMarketplace,
}: {
  template: MarketplaceTemplate;
  targetVersionNumber: number;
  prerequisites: TemplatePrerequisites;
  installStep: InstallProgressStep | null;
  installResult: ImportInstallResult | null;
  installError: string | null;
  installIssues: readonly string[];
  isInstalling: boolean;
  targetDocId: string | undefined;
  databaseBindings: AutomationDatabaseBindings | undefined;
  appBlockTargetKind: 'existing_app' | 'new_app' | undefined;
  appBlockTargetAppId: string | undefined;
  appBlockTargetTitle: string;
  appBlockTargetRootRoutePath: string;
  appBlockDatabaseMappingDecisions: Record<
    string,
    { action: 'create_new' } | { action: 'use_existing'; target: string }
  >;
  appBlockPreflight: AppBlockInstantiationPreflight | null;
  appBlockPreflightLoading: boolean;
  appBlockPreflightError: string | null;
  customApps: readonly { id: string; title: string }[];
  overlayPortal?: HTMLElement | null;
  onTargetDocChange: (docId: string | undefined) => void;
  onDatabaseBindingsChange: (bindings: AutomationDatabaseBindings) => void;
  onAppBlockTargetKindChange: (kind: 'existing_app' | 'new_app') => void;
  onAppBlockTargetAppIdChange: (customAppId: string) => void;
  onAppBlockTargetTitleChange: (title: string) => void;
  onAppBlockTargetRootRoutePathChange: (path: string) => void;
  onAppBlockDatabaseMappingDecisionsChange: (
    decisions: Record<
      string,
      { action: 'create_new' } | { action: 'use_existing'; target: string }
    >
  ) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onOpen: () => void;
  onBackToMarketplace: () => void;
}) {
  const t = useI18n();
  const picker = useLocalePicker();
  const title = picker?.pickTitle(template.titlesI18n) ?? '';
  const selectedVersion = getTemplateVersionMetadata(
    template,
    targetVersionNumber,
    picker
  );
  const permissionPlan =
    prerequisites.permissions ?? prerequisites.dependencies?.permissions;
  const missingPermissionItems = (permissionPlan?.missing ?? []).map(req => ({
    id: `${req.permission}:${req.reason}`,
    title: installPermissionReasonText(req, t),
    meta: t['com.streamlain.marketplace.import.permission.role-meta'](),
    badge: (
      <span className={styles.importListBadgeWarning}>
        {t['com.streamlain.marketplace.import.permission.missing']()}
      </span>
    ),
  }));

  const isDone = installStep === 'done';
  const isError = installStep === 'error';
  const isProgress =
    installStep !== null && installStep !== 'done' && installStep !== 'error';
  const progressLabelKey = installStep
    ? INSTALL_STEP_LABEL_KEYS[installStep]
    : null;
  const isAppBlock = template.templateType === 'app_block';
  const canChooseTargetDoc = usesInstallTargetDoc(template);
  const openDoneLabel =
    template.templateType === 'app_block'
      ? t['com.streamlain.marketplace.import.confirm.open-apps']()
      : t['com.streamlain.marketplace.import.confirm.open']();

  const footer = (
    <div className={styles.installConfirmFooter}>
      {isDone ? (
        <>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onBackToMarketplace}
          >
            {isAppBlock
              ? marketplaceCopy(
                  t,
                  'com.streamlain.marketplace.import.confirm.back-hub',
                  'Zurück zum Hub'
                )
              : t[
                  'com.streamlain.marketplace.import.confirm.back-marketplace'
                ]()}
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={onOpen}
          >
            {openDoneLabel}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className={styles.cancelButton}
            disabled={isInstalling}
            onClick={onCancel}
          >
            {t['com.streamlain.marketplace.installed.action.cancel']()}
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            disabled={
              isInstalling ||
              installIssues.length > 0 ||
              (template.templateType === 'app_block' &&
                (!appBlockPreflight ||
                  !appBlockPreflight.canInstantiate ||
                  appBlockPreflightLoading))
            }
            onClick={onConfirm}
          >
            {isError
              ? t['com.streamlain.marketplace.browse.install.retry']()
              : t['com.streamlain.marketplace.import.action.add']()}
          </button>
        </>
      )}
    </div>
  );

  const panel = (
    <section
      className={styles.installConfirmDialog}
      aria-modal="true"
      role="dialog"
      aria-labelledby="marketplace-install-confirm-title"
    >
      <div className={styles.installConfirmHeader}>
        <div>
          {isDone ? (
            <div className={styles.installConfirmEyebrow}>
              {t['com.streamlain.marketplace.import.confirm.success-eyebrow']()}
            </div>
          ) : null}
          <h2
            id="marketplace-install-confirm-title"
            className={styles.installConfirmTitle}
          >
            {isDone
              ? t['com.streamlain.marketplace.import.confirm.success-title']()
              : t['com.streamlain.marketplace.import.confirm.title']()}
          </h2>
        </div>
        <div className={styles.installConfirmTemplate}>{title}</div>
      </div>

      <div className={styles.installConfirmBody}>
        {isProgress ? (
          <div className={styles.installConfirmState}>
            <div className={styles.spinner} />
            <div>
              <div className={styles.installConfirmStateTitle}>
                {isAppBlock
                  ? marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.app-block.adding',
                      'App-Block wird hinzugefügt …'
                    )
                  : progressLabelKey
                    ? t[progressLabelKey as keyof typeof t]()
                    : ''}
              </div>
              <div className={styles.installConfirmSubtitle}>
                {isAppBlock
                  ? marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.app-block.adding-detail',
                      'Der App-Block und seine benötigten Datenquellen werden atomar angelegt.'
                    )
                  : t['com.streamlain.marketplace.import.confirm.progress']()}
              </div>
            </div>
          </div>
        ) : isDone ? (
          <div className={styles.installConfirmState}>
            <span className={styles.successCheckmark}>{'\u2713'}</span>
            <div>
              <div className={styles.installConfirmStateTitle}>
                {isAppBlock
                  ? marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.app-block.added',
                      'App-Block wurde hinzugefügt.'
                    )
                  : t[
                      'com.streamlain.marketplace.import.confirm.success-subtitle'
                    ]()}
              </div>
              <div className={styles.installConfirmSubtitle}>
                {getInstallSuccessDetail(template, installResult, t)}
              </div>
            </div>
          </div>
        ) : (
          <>
            {template.templateType === 'app_block' ? (
              <section className={styles.installTargetBodySection}>
                <div className={styles.installTargetCopy}>
                  <div className={styles.databaseBindingTitle}>
                    {marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.app-block.catalog.label',
                      'App-Block hinzufügen'
                    )}
                  </div>
                  <div className={styles.databaseBindingText}>
                    {marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.app-block.catalog.body',
                      'Wähle die Ziel-App. Der Block wird direkt und als unabhängige Workspace-Instanz in diese App eingebaut; es entsteht kein Zwischenkatalog.'
                    )}
                  </div>
                  <div className={styles.installTargetGroup}>
                    <label className={styles.installTargetOption}>
                      <input
                        type="radio"
                        name="app-block-target-kind"
                        checked={appBlockTargetKind === 'existing_app'}
                        disabled={isInstalling}
                        onChange={() =>
                          onAppBlockTargetKindChange('existing_app')
                        }
                      />
                      Bestehende App
                    </label>
                    {appBlockTargetKind === 'existing_app' ? (
                      <select
                        className={styles.installTargetSearch}
                        value={appBlockTargetAppId ?? ''}
                        disabled={isInstalling}
                        onChange={event =>
                          onAppBlockTargetAppIdChange(event.target.value)
                        }
                      >
                        <option value="">Ziel-App auswählen</option>
                        {customApps.map(app => (
                          <option key={app.id} value={app.id}>
                            {app.title}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <label className={styles.installTargetOption}>
                      <input
                        type="radio"
                        name="app-block-target-kind"
                        checked={appBlockTargetKind === 'new_app'}
                        disabled={isInstalling}
                        onChange={() => onAppBlockTargetKindChange('new_app')}
                      />
                      Neue App
                    </label>
                    {appBlockTargetKind === 'new_app' ? (
                      <input
                        className={styles.installTargetSearch}
                        value={appBlockTargetTitle}
                        disabled={isInstalling}
                        placeholder="Name der neuen App"
                        onChange={event =>
                          onAppBlockTargetTitleChange(event.target.value)
                        }
                      />
                    ) : null}
                    <label className={styles.databaseBindingText}>
                      Route in der Ziel-App
                      <input
                        className={styles.installTargetSearch}
                        value={appBlockTargetRootRoutePath}
                        disabled={isInstalling}
                        placeholder="/"
                        onChange={event =>
                          onAppBlockTargetRootRoutePathChange(
                            event.target.value
                          )
                        }
                      />
                    </label>
                    {appBlockPreflightLoading ? (
                      <div className={styles.databaseBindingText}>
                        Preflight wird geladen …
                      </div>
                    ) : null}
                    {appBlockPreflightError ? (
                      <div className={styles.installErrorAlert}>
                        {appBlockPreflightError}
                      </div>
                    ) : null}
                    {appBlockPreflight ? (
                      <div className={styles.installTargetGroup}>
                        <div className={styles.databaseBindingText}>
                          Zielroute:{' '}
                          {appBlockPreflight.plan.targetRootRoutePath}
                          {' · '}Routen:{' '}
                          {appBlockPreflight.plan.routePaths.join(', ') ||
                            'keine'}
                          {' · '}Automationen:{' '}
                          {appBlockPreflight.plan.automationMappings.length}
                          {' · '}Credentials:{' '}
                          {appBlockPreflight.plan.credentialMappings.length}
                        </div>
                        {appBlockPreflight.conflicts.length > 0 ? (
                          <div className={styles.installErrorAlert}>
                            {appBlockPreflight.conflicts.join('; ')}
                          </div>
                        ) : null}
                        {appBlockPreflight.plan.dataSourceMappings.map(
                          mapping => {
                            const decision =
                              appBlockDatabaseMappingDecisions[
                                mapping.source
                              ] ?? ({ action: 'create_new' } as const);
                            return (
                              <label
                                key={mapping.source}
                                className={styles.databaseBindingText}
                              >
                                Datenquelle „{mapping.source}“
                                <select
                                  className={styles.installTargetSearch}
                                  value={decision.action}
                                  disabled={isInstalling}
                                  onChange={event => {
                                    const action = event.target.value;
                                    onAppBlockDatabaseMappingDecisionsChange({
                                      ...appBlockDatabaseMappingDecisions,
                                      [mapping.source]:
                                        action === 'use_existing'
                                          ? {
                                              action: 'use_existing',
                                              target: mapping.target,
                                            }
                                          : { action: 'create_new' },
                                    });
                                  }}
                                >
                                  <option value="create_new">
                                    Neue App-eigene Datenquelle
                                  </option>
                                  <option value="use_existing">
                                    Vorhandene Datenquelle verwenden
                                  </option>
                                </select>
                                {decision.action === 'use_existing' ? (
                                  <input
                                    className={styles.installTargetSearch}
                                    value={decision.target}
                                    disabled={isInstalling}
                                    placeholder="Ziel-Source-Key"
                                    onChange={event =>
                                      onAppBlockDatabaseMappingDecisionsChange({
                                        ...appBlockDatabaseMappingDecisions,
                                        [mapping.source]: {
                                          action: 'use_existing',
                                          target: event.target.value,
                                        },
                                      })
                                    }
                                  />
                                ) : null}
                              </label>
                            );
                          }
                        )}
                      </div>
                    ) : null}
                    <div className={styles.databaseBindingText}>
                      Benötigte Datenbanken werden als neue, App-eigene Quellen
                      angelegt. Die App bleibt dabei im Entwurf und wird nicht
                      veröffentlicht.
                    </div>
                  </div>
                </div>
              </section>
            ) : canChooseTargetDoc ? (
              <section className={styles.installTargetBodySection}>
                <div className={styles.installTargetCopy}>
                  <div className={styles.databaseBindingTitle}>
                    {t['com.streamlain.marketplace.import.target.label']()}
                  </div>
                  <div className={styles.databaseBindingText}>
                    {marketplaceCopy(
                      t,
                      'com.streamlain.marketplace.import.target.body',
                      'Wähle, ob die Vorlage in ein neues oder ein vorhandenes Dokument installiert wird.'
                    )}
                  </div>
                </div>
                <InstallTargetPicker
                  targetDocId={targetDocId}
                  onTargetDocChange={onTargetDocChange}
                  disabled={isInstalling}
                />
              </section>
            ) : null}
            {selectedVersion?.changelog ? (
              <section className={styles.installVersionNotesSection}>
                <div className={styles.databaseBindingTitle}>
                  {t['com.streamlain.marketplace.import.version-notes.title']()}
                </div>
                <div className={styles.installVersionNotesMeta}>
                  {t['com.streamlain.marketplace.import.version-notes.version'](
                    {
                      version: selectedVersion.semanticVersion,
                    }
                  )}
                </div>
                <div className={styles.installVersionNotesText}>
                  {selectedVersion.changelog}
                </div>
              </section>
            ) : null}
            {template.templateType === 'automation' && targetDocId ? (
              <AutomationDatabaseBindingPanel
                bindings={databaseBindings}
                targetDocId={targetDocId}
                disabled={isInstalling}
                onChange={onDatabaseBindingsChange}
              />
            ) : null}
            {installIssues.length > 0 ? (
              <div className={styles.credentialWarning}>
                {t['com.streamlain.marketplace.import.confirm.blocked']({
                  count: String(installIssues.length),
                })}
              </div>
            ) : null}
            {missingPermissionItems.length > 0 ? (
              <>
                <div className={styles.credentialWarning}>
                  {t['com.streamlain.marketplace.import.permission.blocked']()}
                </div>
                <InstallConfirmSection
                  title={t[
                    'com.streamlain.marketplace.import.confirm.permissions'
                  ]()}
                  subtitle={t[
                    'com.streamlain.marketplace.import.permission.subtitle'
                  ]()}
                  items={missingPermissionItems}
                />
              </>
            ) : null}
            {installError ? (
              <div className={styles.installErrorAlert} role="alert">
                {installError}
              </div>
            ) : null}
          </>
        )}
      </div>

      {footer}
    </section>
  );

  const dialog = (
    <div className={styles.installConfirmBackdrop} role="presentation">
      {panel}
    </div>
  );

  return overlayPortal ? createPortal(dialog, overlayPortal) : dialog;
}

// =============================================================================
// ImportSetupWizard
// =============================================================================

interface WizardCommonProps {
  template: MarketplaceTemplate;
  workspaceId: string;
  canRate?: boolean;
  autoFocusRating?: boolean;
  chrome?: 'dialog' | 'page';
  actionPortal?: HTMLElement | null;
  overlayPortal?: HTMLElement | null;
  onBack?: () => void;
  onCancel: () => void;
}

export interface InstallWizardProps extends WizardCommonProps {
  mode?: 'install';
  onInstall: (
    bindings: ConcretizationBindings,
    options: InstallOptions
  ) => Promise<void>;
  onRate: (
    templateId: string,
    rating: number,
    comment?: string
  ) => Promise<unknown>;
  isInstalling: boolean;
  installError: string | null;
  installStep: InstallProgressStep | null;
  installResult: ImportInstallResult | null;
  onInstallOpen: () => void;
  onInstallBackToMarketplace: () => void;
  onInstallReset: () => void;
}

export interface ManageWizardProps extends WizardCommonProps {
  mode: 'manage';
  installedInfo: InstalledTemplateInfo;
  management: InstalledTemplateManagement;
  onOpen: () => void;
  onOpenWorkflow: () => void;
  onAddAnother: () => void;
  onUpdate: () => void;
  onRate: (
    templateId: string,
    rating: number,
    comment?: string
  ) => Promise<unknown>;
}

export type ImportSetupWizardProps = InstallWizardProps | ManageWizardProps;

export function ImportSetupWizard(props: ImportSetupWizardProps) {
  if (props.mode === 'manage') {
    return <ManageWizardBody {...props} />;
  }
  return <InstallWizardBody {...props} />;
}

function InstallWizardBody({
  template,
  workspaceId,
  canRate = true,
  autoFocusRating = false,
  chrome = 'dialog',
  actionPortal,
  overlayPortal,
  onInstall,
  onBack,
  onCancel,
  isInstalling,
  installError,
  installStep,
  installResult,
  onRate,
  onInstallOpen,
  onInstallBackToMarketplace,
  onInstallReset,
}: InstallWizardProps) {
  const t = useI18n();
  const graphql = useService(GraphQLService);
  const picker = useLocalePicker();
  const isAutomation = template.templateType === 'automation';
  const canChooseTargetDoc = usesInstallTargetDoc(template);
  const isPageChrome = chrome === 'page';
  const latestVersion = useMemo(
    () => resolveTemplateLatestVersion(template),
    [template]
  );
  const [targetVersionNumber, setTargetVersionNumber] = useState(latestVersion);
  useEffect(() => {
    setTargetVersionNumber(latestVersion);
  }, [latestVersion, template.id]);
  const [isVersionMenuOpen, setVersionMenuOpen] = useState(false);
  const installVersionOptions = useMemo(
    () =>
      buildInstallVersionOptions({
        template,
        latestVersion,
      }),
    [latestVersion, template]
  );
  const selectedVersionLabel = useMemo(
    () => installVersionLabel(t, template, targetVersionNumber, latestVersion),
    [latestVersion, t, targetVersionNumber, template]
  );

  // sl-qndg.5.3: Load prerequisites (credentials + config vars) from backend
  const {
    prerequisites,
    isLoading: prerequisitesLoading,
    error: prerequisitesError,
  } = useTemplatePrerequisites(workspaceId, template.id, targetVersionNumber);
  const triggerFields = useMemo(
    () => prerequisites?.parameterizedTriggerFields ?? [],
    [prerequisites]
  );
  const promptableTriggerFields = useMemo(
    () => triggerFields.filter(f => f.default !== 'GENERATE_AT_INSTALL'),
    [triggerFields]
  );

  // Config values (seeded from prerequisites defaults)
  const [configValues, setConfigValues] = useState<Record<string, string>>({});

  // sl-mkx.5.3: installer-supplied trigger values (cron / db_column)
  const [triggerValues, setTriggerValues] = useState<Record<string, string>>(
    {}
  );
  const [isInstallDialogOpen, setInstallDialogOpen] = useState(false);
  const [targetDocId, setTargetDocId] = useState<string | undefined>();
  const deepLinkedAppBlockTarget = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const customAppId = params.get('targetCustomAppId')?.trim();
    if (!customAppId) return null;
    const routePath = params.get('targetRootRoutePath')?.trim() || '/';
    return { customAppId, routePath };
  }, []);
  const [appBlockTargetKind, setAppBlockTargetKind] = useState<
    'existing_app' | 'new_app' | undefined
  >(deepLinkedAppBlockTarget ? 'existing_app' : undefined);
  const [appBlockTargetAppId, setAppBlockTargetAppId] = useState<
    string | undefined
  >(deepLinkedAppBlockTarget?.customAppId);
  const [appBlockTargetTitle, setAppBlockTargetTitle] = useState('');
  const [appBlockTargetRootRoutePath, setAppBlockTargetRootRoutePath] =
    useState(deepLinkedAppBlockTarget?.routePath ?? '/');
  const [
    appBlockDatabaseMappingDecisions,
    setAppBlockDatabaseMappingDecisions,
  ] = useState<
    Record<
      string,
      { action: 'create_new' } | { action: 'use_existing'; target: string }
    >
  >({});
  const [appBlockPreflight, setAppBlockPreflight] =
    useState<AppBlockInstantiationPreflight | null>(null);
  const [appBlockPreflightLoading, setAppBlockPreflightLoading] =
    useState(false);
  const [appBlockPreflightError, setAppBlockPreflightError] = useState<
    string | null
  >(null);
  const [customApps, setCustomApps] = useState<
    readonly { id: string; title: string }[]
  >([]);
  const [appBlockOperationId, setAppBlockOperationId] = useState(() =>
    crypto.randomUUID()
  );
  const [databaseBindings, setDatabaseBindings] = useState<
    AutomationDatabaseBindings | undefined
  >();
  const [isRatingDialogOpen, setRatingDialogOpen] = useState(false);
  const [hasRated, setHasRated] = useState(false);

  useEffect(() => {
    setAppBlockDatabaseMappingDecisions({});
  }, [
    appBlockOperationId,
    appBlockTargetAppId,
    appBlockTargetKind,
    appBlockTargetTitle,
  ]);

  useEffect(() => {
    if (template.templateType !== 'app_block' || !isInstallDialogOpen) {
      setAppBlockPreflight(null);
      setAppBlockPreflightError(null);
      return;
    }
    const target =
      appBlockTargetKind === 'existing_app' && appBlockTargetAppId
        ? {
            kind: 'existing_app' as const,
            customAppId: appBlockTargetAppId,
          }
        : appBlockTargetKind === 'new_app' && appBlockTargetTitle.trim()
          ? {
              kind: 'new_app' as const,
              title: appBlockTargetTitle.trim(),
              appKind: 'workspace' as const,
            }
          : null;
    if (!target) {
      setAppBlockPreflight(null);
      setAppBlockPreflightError(null);
      return;
    }

    const controller = new AbortController();
    setAppBlockPreflight(null);
    setAppBlockPreflightLoading(true);
    setAppBlockPreflightError(null);
    const endpoint = `/api/workspace/${workspaceId}/marketplace/templates/${template.id}/app-block-instantiation-preflight`;
    void workspaceApiFetch(
      endpoint,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operationId: appBlockOperationId,
          target,
          targetRootRoutePath: appBlockTargetRootRoutePath,
          targetVersionNumber,
          ...(Object.keys(appBlockDatabaseMappingDecisions).length > 0
            ? {
                databaseMappingDecisions: appBlockDatabaseMappingDecisions,
              }
            : {}),
        }),
        signal: controller.signal,
      },
      { workspaceId, mode: 'foreground' }
    )
      .then(async response => {
        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as {
            message?: string;
          };
          throw new Error(
            body.message ?? `App Block preflight failed: ${response.status}`
          );
        }
        const next = (await response.json()) as AppBlockInstantiationPreflight;
        if (Object.keys(appBlockDatabaseMappingDecisions).length === 0) {
          const defaults = Object.fromEntries(
            next.plan.dataSourceMappings.map(mapping => [
              mapping.source,
              mapping.action === 'reuse' || mapping.action === 'use_existing'
                ? { action: 'use_existing' as const, target: mapping.target }
                : { action: 'create_new' as const },
            ])
          );
          if (Object.keys(defaults).length > 0) {
            setAppBlockDatabaseMappingDecisions(defaults);
            return;
          }
        }
        setAppBlockPreflight(next);
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        setAppBlockPreflightError(
          error instanceof Error ? error.message : String(error)
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setAppBlockPreflightLoading(false);
      });

    return () => controller.abort();
  }, [
    appBlockDatabaseMappingDecisions,
    appBlockOperationId,
    appBlockTargetAppId,
    appBlockTargetKind,
    appBlockTargetRootRoutePath,
    appBlockTargetTitle,
    isInstallDialogOpen,
    targetVersionNumber,
    template.id,
    template.templateType,
    workspaceId,
  ]);
  const handleRatingAction = useCallback(() => {
    setRatingDialogOpen(true);
  }, []);

  useEffect(() => {
    if (!autoFocusRating || !canRate || prerequisitesLoading) return;
    const frame = window.requestAnimationFrame(handleRatingAction);
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocusRating, canRate, handleRatingAction, prerequisitesLoading]);

  useEffect(() => {
    setInstallDialogOpen(false);
    setTargetDocId(undefined);
    setDatabaseBindings(undefined);
    setAppBlockTargetKind(
      deepLinkedAppBlockTarget ? 'existing_app' : undefined
    );
    setAppBlockTargetAppId(deepLinkedAppBlockTarget?.customAppId);
    setAppBlockTargetRootRoutePath(deepLinkedAppBlockTarget?.routePath ?? '/');
    setAppBlockTargetTitle(picker?.pickTitle(template.titlesI18n) ?? '');
    setAppBlockOperationId(crypto.randomUUID());
    setRatingDialogOpen(false);
    setHasRated(false);
  }, [deepLinkedAppBlockTarget, picker, template.id, template.titlesI18n]);

  useEffect(() => {
    if (template.templateType !== 'app_block') return;
    let active = true;
    void graphql
      .gql({ query: customAppsQuery, variables: { workspaceId } })
      .then(result => {
        if (!active) return;
        setCustomApps(
          (result.customApps ?? []).map(app => ({
            id: app.id,
            title: app.title,
          }))
        );
      })
      .catch(() => {
        if (active) setCustomApps([]);
      });
    return () => {
      active = false;
    };
  }, [graphql, template.templateType, workspaceId]);

  useEffect(() => {
    if (!isAutomation || !prerequisites) {
      setDatabaseBindings(undefined);
      return;
    }
    setDatabaseBindings(
      createAutomationDatabaseBindings(prerequisites.columnPreview, targetDocId)
    );
  }, [isAutomation, prerequisites, targetDocId]);

  const handleTargetDocChange = useCallback((docId: string | undefined) => {
    setTargetDocId(docId);
    setDatabaseBindings(current =>
      current
        ? updateAutomationDatabaseBindingTargetDoc(current, docId)
        : undefined
    );
  }, []);

  // Pre-fill config defaults once prerequisites arrive
  useEffect(() => {
    if (!prerequisites) return;
    setConfigValues(prev => {
      const next = { ...prev };
      for (const v of prerequisites.config) {
        if (v.defaultValue !== undefined && next[v.key] === undefined) {
          next[v.key] = v.defaultValue;
        }
      }
      return next;
    });
  }, [prerequisites]);

  // sl-mkx.5.3: seed trigger defaults so the installer receives the
  // publisher's recommended values without reintroducing a multi-step wizard.
  useEffect(() => {
    if (promptableTriggerFields.length === 0) return;
    setTriggerValues(prev => {
      const next = { ...prev };
      for (const f of promptableTriggerFields) {
        if (next[f.fieldRef] === undefined && f.default !== undefined) {
          next[f.fieldRef] = f.default;
        }
      }
      return next;
    });
  }, [promptableTriggerFields]);

  const missingDependencies = useMemo(
    () =>
      (prerequisites?.dependencies?.dependencies ?? []).filter(
        dep => dep.status === 'missing'
      ),
    [prerequisites?.dependencies?.dependencies]
  );
  const versionConflictDependencies = useMemo(
    () =>
      (prerequisites?.dependencies?.dependencies ?? []).filter(
        dep => dep.status === 'version_conflict'
      ),
    [prerequisites?.dependencies?.dependencies]
  );

  const handleInstall = useCallback(async () => {
    const bindings: ConcretizationBindings = {
      credentialMappings: {},
      configValues,
      ...(missingDependencies.length > 0
        ? {
            confirmedDependencies: missingDependencies.map(dep => ({
              templateId: dep.templateId,
              versionNumber: dep.versionNumber,
              canonicalKey: dep.canonicalKey,
              majorVersion: dep.majorVersion,
              semanticVersion: dep.semanticVersion,
              contractHash: dep.contractHash,
            })),
          }
        : {}),
      ...(Object.keys(triggerValues).length > 0 ? { triggerValues } : {}),
    };
    const options: InstallOptions = {
      targetVersionNumber,
      ...(canChooseTargetDoc && targetDocId ? { targetDocId } : {}),
      ...(isAutomation
        ? {
            ...getAutomationDatabaseCreateFlags(databaseBindings),
            ...(hasExistingDatabaseBindings(databaseBindings)
              ? { databaseBindings }
              : {}),
          }
        : {}),
      ...(template.templateType === 'app_block' && appBlockTargetKind
        ? {
            appBlockTarget:
              appBlockTargetKind === 'existing_app'
                ? {
                    operationId: appBlockOperationId,
                    kind: 'existing_app' as const,
                    customAppId: appBlockTargetAppId ?? '',
                  }
                : {
                    operationId: appBlockOperationId,
                    kind: 'new_app' as const,
                    title: appBlockTargetTitle.trim(),
                    appKind: 'workspace' as const,
                  },
            appBlockTargetRootRoutePath,
            appBlockDatabaseMappingDecisions,
            ...(appBlockPreflight
              ? {
                  appBlockConfirmedPreflightDigest:
                    appBlockPreflight.preflightDigest,
                }
              : {}),
          }
        : {}),
    };
    await onInstall(bindings, options);
  }, [
    configValues,
    triggerValues,
    missingDependencies,
    canChooseTargetDoc,
    isAutomation,
    databaseBindings,
    targetVersionNumber,
    targetDocId,
    template.templateType,
    appBlockOperationId,
    appBlockTargetAppId,
    appBlockTargetKind,
    appBlockTargetTitle,
    appBlockTargetRootRoutePath,
    appBlockDatabaseMappingDecisions,
    appBlockPreflight,
    onInstall,
  ]);

  const handleInstallDialogCancel = useCallback(() => {
    setInstallDialogOpen(false);
    if (installStep === 'error') {
      onInstallReset();
    }
  }, [installStep, onInstallReset]);

  const installIssues = useMemo(() => {
    const issues: string[] = [];
    if (!prerequisites) return issues;

    if (template.templateType === 'app_block') {
      if (!appBlockTargetKind) {
        issues.push('Wähle eine bestehende oder eine neue Ziel-App.');
      } else if (
        appBlockTargetKind === 'existing_app' &&
        !appBlockTargetAppId
      ) {
        issues.push('Wähle die bestehende Ziel-App aus.');
      } else if (
        appBlockTargetKind === 'new_app' &&
        !appBlockTargetTitle.trim()
      ) {
        issues.push('Gib einen Namen für die neue Ziel-App ein.');
      }
    }

    for (const cred of prerequisites.credentials.filter(
      c => c.required && !c.isProvidedByStreamlain
    )) {
      issues.push(
        t['com.streamlain.marketplace.import.issue.missing-credential']({
          label: cred.label,
        })
      );
    }

    for (const value of prerequisites.config) {
      if (value.required && !configValues[value.key] && !value.defaultValue) {
        issues.push(
          t['com.streamlain.marketplace.import.issue.missing-config']({
            key: value.key,
          })
        );
      }
    }

    for (const field of promptableTriggerFields) {
      if (
        field.required &&
        (triggerValues[field.fieldRef] ?? '').trim() === ''
      ) {
        issues.push(
          t['com.streamlain.marketplace.import.issue.missing-trigger']({
            label: field.label,
          })
        );
      }
    }

    const missingPermissions =
      prerequisites.permissions?.missing ??
      prerequisites.dependencies?.permissions?.missing ??
      [];
    for (const permission of missingPermissions) {
      issues.push(
        t['com.streamlain.marketplace.import.issue.missing-permission']({
          reason: installPermissionReasonText(permission, t),
        })
      );
    }

    for (const dependency of versionConflictDependencies) {
      const installedSemanticVersion = dependency.installedSemanticVersion;
      const requiredSemanticVersion = dependency.semanticVersion;
      issues.push(
        marketplaceCopy(
          t,
          installedSemanticVersion
            ? 'com.streamlain.marketplace.import.issue.dependency-version-conflict'
            : 'com.streamlain.marketplace.import.issue.dependency-version-conflict-generic',
          installedSemanticVersion
            ? '{{title}} ist bereits als Version {{installed}} installiert, benötigt wird Version {{required}}.'
            : '{{title}} ist bereits in einer anderen Version installiert.',
          {
            title: dependency.title,
            installed: installedSemanticVersion ?? '',
            required: requiredSemanticVersion,
          }
        )
      );
    }

    if (hasExistingDatabaseBindings(databaseBindings)) {
      for (const surface of databaseBindings?.surfaces ?? []) {
        if (surface.mode !== 'use_existing') continue;
        if (!surface.targetDatabaseBlockId) {
          issues.push(
            marketplaceCopy(
              t,
              'com.streamlain.marketplace.import.issue.missing-database-binding',
              'Für jede bestehende Datenbank-Zuordnung muss eine Datenbank ausgewählt sein.'
            )
          );
          break;
        }
        if (
          surface.columns.some(
            column => column.action === 'map_existing' && !column.targetColumnId
          )
        ) {
          issues.push(
            marketplaceCopy(
              t,
              'com.streamlain.marketplace.import.issue.missing-column-binding',
              'Für gesperrte oder explizit zugeordnete Datenbanken müssen alle benötigten Spalten auf bestehende Spalten gemappt sein.'
            )
          );
          break;
        }
      }
    }

    return issues;
  }, [
    configValues,
    databaseBindings,
    appBlockTargetAppId,
    appBlockTargetKind,
    appBlockTargetTitle,
    prerequisites,
    promptableTriggerFields,
    t,
    template.templateType,
    triggerValues,
    versionConflictDependencies,
  ]);

  const canOpenInstallDialog =
    !prerequisitesLoading && prerequisites !== null && !isInstalling;

  const showInstallDialog =
    prerequisites !== null && (isInstallDialogOpen || installStep !== null);
  const addLabel = isInstalling
    ? template.templateType === 'app_block'
      ? marketplaceCopy(
          t,
          'com.streamlain.marketplace.import.app-block.adding',
          'App-Block wird hinzugefügt …'
        )
      : t['com.streamlain.marketplace.import.installing']()
    : template.templateType === 'step'
      ? t['com.streamlain.marketplace.import.action.add.step']()
      : t['com.streamlain.marketplace.import.action.add']();
  const actions = (
    <div className={styles.pagination}>
      <button
        type="button"
        className={styles.cancelButton}
        onClick={onCancel}
        disabled={isInstalling}
      >
        {isPageChrome
          ? t['com.streamlain.marketplace.browse.detail.back']()
          : t['com.streamlain.marketplace.installed.action.cancel']()}
      </button>
      {template.isPreinstalled ? (
        <span className={styles.manageStatusPill}>
          <CheckIcon size={12} />
          {template.templateType === 'step'
            ? t['com.streamlain.marketplace.installed.status.system']()
            : t['com.streamlain.marketplace.installed.status.preinstalled']()}
        </span>
      ) : (
        <div className={styles.installSplitButtonRoot}>
          <button
            type="button"
            className={styles.installSplitButtonMain}
            disabled={!canOpenInstallDialog}
            onClick={() => setInstallDialogOpen(true)}
          >
            <span>{addLabel}</span>
            {installVersionOptions.length > 1 ? (
              <span className={styles.installSplitButtonVersion}>
                {selectedVersionLabel}
              </span>
            ) : null}
          </button>
          {installVersionOptions.length > 1 ? (
            <Popover.Root
              open={isVersionMenuOpen}
              onOpenChange={setVersionMenuOpen}
            >
              <Popover.Trigger asChild>
                <button
                  type="button"
                  className={styles.installSplitButtonToggle}
                  disabled={isInstalling}
                  aria-label={t[
                    'com.streamlain.marketplace.import.version.label'
                  ]()}
                >
                  <ChevronDownIcon size={14} />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  align="end"
                  side="top"
                  sideOffset={8}
                  className={styles.installVersionMenu}
                >
                  <div className={styles.installVersionMenuLabel}>
                    {t['com.streamlain.marketplace.import.version.label']()}
                  </div>
                  {installVersionOptions.map(versionNumber => (
                    <button
                      key={versionNumber}
                      type="button"
                      className={
                        versionNumber === targetVersionNumber
                          ? `${styles.installVersionMenuItem} ${styles.installVersionMenuItemActive}`
                          : styles.installVersionMenuItem
                      }
                      onClick={() => {
                        setTargetVersionNumber(versionNumber);
                        setVersionMenuOpen(false);
                      }}
                    >
                      <span>
                        {installVersionLabel(
                          t,
                          template,
                          versionNumber,
                          latestVersion
                        )}
                      </span>
                      {versionNumber === targetVersionNumber ? (
                        <CheckIcon size={14} />
                      ) : null}
                    </button>
                  ))}
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          ) : null}
        </div>
      )}
    </div>
  );
  const actionFooter = isPageChrome
    ? actionPortal
      ? createPortal(actions, actionPortal)
      : null
    : actions;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: isPageChrome ? 'auto' : '100%',
        minHeight: isPageChrome ? '100%' : undefined,
        position: 'relative',
      }}
    >
      {!isPageChrome ? (
        <div className={styles.dialogHeader}>
          {onBack ? (
            <button
              className={styles.backButton}
              disabled={isInstalling}
              onClick={onBack}
              type="button"
            >
              <ArrowLeftIcon size={14} />
              {t['com.streamlain.marketplace.browse.detail.back']()}
            </button>
          ) : null}
          <span className={styles.dialogTitle}>
            {t['com.streamlain.marketplace.import.title']()}
          </span>
        </div>
      ) : null}

      {/* Template impact preview */}
      <div
        style={{
          flex: isPageChrome ? 'none' : 1,
          minHeight: 0,
          overflow: isPageChrome ? 'visible' : 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <StepPreview
          template={template}
          workspaceId={workspaceId}
          targetVersionNumber={targetVersionNumber}
          prerequisites={prerequisites}
          isLoading={prerequisitesLoading}
          error={prerequisitesError}
          onRatingAction={canRate ? handleRatingAction : undefined}
          containedScroll={!isPageChrome}
        />
      </div>
      {canRate ? (
        <RatingDialog
          open={isRatingDialogOpen}
          templateId={template.id}
          averageRating={template.averageRating ?? 0}
          alreadyRated={hasRated}
          onClose={() => setRatingDialogOpen(false)}
          onSubmitted={() => setHasRated(true)}
          onRate={onRate}
        />
      ) : null}

      {/* Single install action — preinstalled system templates can't be
          re-added meaningfully; show a "system / already available" pill
          instead of a misleading install CTA. */}
      {actionFooter}
      {showInstallDialog && prerequisites ? (
        <InstallConfirmationDialog
          template={template}
          targetVersionNumber={targetVersionNumber}
          prerequisites={prerequisites}
          installStep={installStep}
          installResult={installResult}
          installError={installError}
          installIssues={installIssues}
          isInstalling={isInstalling}
          targetDocId={targetDocId}
          databaseBindings={databaseBindings}
          appBlockTargetKind={appBlockTargetKind}
          appBlockTargetAppId={appBlockTargetAppId}
          appBlockTargetTitle={appBlockTargetTitle}
          appBlockTargetRootRoutePath={appBlockTargetRootRoutePath}
          appBlockDatabaseMappingDecisions={appBlockDatabaseMappingDecisions}
          appBlockPreflight={appBlockPreflight}
          appBlockPreflightLoading={appBlockPreflightLoading}
          appBlockPreflightError={appBlockPreflightError}
          customApps={customApps}
          overlayPortal={overlayPortal}
          onTargetDocChange={handleTargetDocChange}
          onDatabaseBindingsChange={setDatabaseBindings}
          onAppBlockTargetKindChange={setAppBlockTargetKind}
          onAppBlockTargetAppIdChange={setAppBlockTargetAppId}
          onAppBlockTargetTitleChange={setAppBlockTargetTitle}
          onAppBlockTargetRootRoutePathChange={setAppBlockTargetRootRoutePath}
          onAppBlockDatabaseMappingDecisionsChange={
            setAppBlockDatabaseMappingDecisions
          }
          onCancel={handleInstallDialogCancel}
          onConfirm={() => void handleInstall()}
          onOpen={onInstallOpen}
          onBackToMarketplace={onInstallBackToMarketplace}
        />
      ) : null}
    </div>
  );
}

function ManageWizardBody({
  template,
  workspaceId,
  canRate = true,
  autoFocusRating = false,
  chrome = 'dialog',
  actionPortal,
  installedInfo,
  management,
  onBack,
  onCancel,
  onOpen,
  onOpenWorkflow,
  onAddAnother,
  onUpdate,
  onRate,
}: ManageWizardProps) {
  const t = useI18n();
  const isPageChrome = chrome === 'page';
  const {
    prerequisites,
    isLoading: prerequisitesLoading,
    error: prerequisitesError,
  } = useTemplatePrerequisites(workspaceId, template.id);
  const [isRatingDialogOpen, setRatingDialogOpen] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const handleRatingAction = useCallback(() => {
    setRatingDialogOpen(true);
  }, []);

  useEffect(() => {
    setRatingDialogOpen(false);
    setHasRated(false);
  }, [template.id]);

  useEffect(() => {
    if (!autoFocusRating || !canRate || prerequisitesLoading) return;
    const frame = window.requestAnimationFrame(handleRatingAction);
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocusRating, canRate, handleRatingAction, prerequisitesLoading]);

  // Only document templates retain an explicit reinstall flow. Every other
  // Marketplace install is an immutable workspace snapshot.
  const hasUpdate =
    template.templateType === 'doc' &&
    installedInfo.latestVersion > installedInfo.versionNumber;
  const canAddAnother = isRepeatableBlueprint(template);
  const primaryLabel = managePrimaryActionLabel(t, management);
  const statusLabel = management.isPreinstalled
    ? t['com.streamlain.marketplace.installed.status.preinstalled']()
    : t['com.streamlain.marketplace.installed.status.installed']();

  const artifactKinds = new Set(installedInfo.artifactSummary?.artifactKinds);
  const typeKey =
    `com.streamlain.marketplace.card.type.${management.templateType}` as keyof typeof t;
  const typeLabel =
    typeof t[typeKey] === 'function'
      ? (t[typeKey] as () => string)()
      : management.templateType;
  const artifactRows = [
    artifactKinds.has('doc')
      ? {
          key: 'docs',
          label: t['com.streamlain.marketplace.installed.artifact.docs']({
            count: String(
              installedInfo.artifactSummary?.docCount ??
                installedInfo.installedDocIds.length ??
                0
            ),
          }),
          active: true,
        }
      : null,
    artifactKinds.has('workflow')
      ? {
          key: 'workflow',
          label: t['com.streamlain.marketplace.installed.artifact.workflow'](),
          active: !!management.workflowId,
        }
      : null,
    management.templateType === 'step' || artifactKinds.has('step')
      ? {
          key: 'step',
          label: t['com.streamlain.marketplace.installed.artifact.step'](),
          active: true,
        }
      : null,
  ].filter((row): row is { key: string; label: string; active: boolean } =>
    Boolean(row)
  );
  const fallbackArtifactRows =
    artifactRows.length > 0
      ? artifactRows
      : [
          {
            key: management.templateType,
            label: typeLabel,
            active: true,
          },
        ];

  const statusPill = (
    <span className={styles.manageStatusPill}>
      <CheckIcon size={12} />
      {statusLabel}
    </span>
  );
  const actions = (
    <div className={styles.pagination} style={{ justifyContent: 'flex-end' }}>
      <button
        type="button"
        className={styles.cancelButton}
        onClick={onCancel}
        style={{ marginRight: 'auto' }}
      >
        {isPageChrome
          ? t['com.streamlain.marketplace.browse.detail.back']()
          : t['com.streamlain.marketplace.installed.action.cancel']()}
      </button>
      {canAddAnother && (
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onAddAnother}
        >
          {t['com.streamlain.marketplace.installed.action.add-again']()}
        </button>
      )}
      {hasUpdate && (
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onUpdate}
        >
          {t['com.streamlain.marketplace.installed.update.label']({
            version: requireInstalledLatestSemanticVersion(installedInfo),
          })}
        </button>
      )}
      {management.canOpenDoc &&
        management.canOpenWorkflow &&
        management.workflowId && (
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onOpenWorkflow}
          >
            {t['com.streamlain.marketplace.installed.action.open-workflow']()}
          </button>
        )}
      {management.primaryAction !== 'none' && (
        <button type="button" className={styles.confirmButton} onClick={onOpen}>
          {primaryLabel}
        </button>
      )}
    </div>
  );
  const actionFooter = isPageChrome
    ? actionPortal
      ? createPortal(actions, actionPortal)
      : null
    : actions;

  const artifactsCard = (
    <PreviewImpactCard
      title={t['com.streamlain.marketplace.installed.manage.artifacts']()}
    >
      <div className={styles.manageArtifactList}>
        {fallbackArtifactRows.map(row => (
          <div key={row.key} className={styles.manageArtifactRow}>
            <span
              className={row.active ? styles.manageDotActive : styles.manageDot}
            />
            {row.label}
          </div>
        ))}
      </div>
    </PreviewImpactCard>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: isPageChrome ? 'auto' : '100%',
        minHeight: isPageChrome ? '100%' : undefined,
        position: 'relative',
      }}
    >
      {!isPageChrome ? (
        <div className={styles.dialogHeader}>
          {onBack ? (
            <button
              className={styles.backButton}
              onClick={onBack}
              type="button"
            >
              <ArrowLeftIcon size={14} />
              {t['com.streamlain.marketplace.browse.detail.back']()}
            </button>
          ) : null}
          <span className={styles.dialogTitle}>
            {t['com.streamlain.marketplace.installed.manage.title']()}
          </span>
        </div>
      ) : null}

      <div
        style={{
          flex: isPageChrome ? 'none' : 1,
          minHeight: 0,
          overflow: isPageChrome ? 'visible' : 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <StepPreview
          template={template}
          workspaceId={workspaceId}
          prerequisites={prerequisites}
          isLoading={prerequisitesLoading}
          error={prerequisitesError}
          onRatingAction={canRate ? handleRatingAction : undefined}
          headerExtra={statusPill}
          containedScroll={!isPageChrome}
          extraCards={
            <>
              {management.templateType === 'automation' ||
              management.templateType === 'workflow'
                ? null
                : artifactsCard}
            </>
          }
        />
      </div>
      {canRate ? (
        <RatingDialog
          open={isRatingDialogOpen}
          templateId={template.id}
          averageRating={template.averageRating ?? 0}
          alreadyRated={hasRated}
          onClose={() => setRatingDialogOpen(false)}
          onSubmitted={() => setHasRated(true)}
          onRate={onRate}
        />
      ) : null}

      {actionFooter}
    </div>
  );
}

export function RatingDialog({
  open,
  templateId,
  averageRating,
  alreadyRated,
  blockedMessage,
  onClose,
  onSubmitted,
  onRate,
}: {
  open: boolean;
  templateId: string;
  averageRating: number;
  alreadyRated: boolean;
  blockedMessage?: string | null;
  onClose: () => void;
  onSubmitted: () => void;
  onRate: (
    templateId: string,
    rating: number,
    comment?: string
  ) => Promise<unknown>;
}) {
  const t = useI18n();
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const display = hoverValue ?? selected ?? 0;
  const locked = Boolean(blockedMessage) || alreadyRated || submitted;

  useEffect(() => {
    setHoverValue(null);
    setSelected(null);
    setComment('');
    setError(null);
    setSubmitting(false);
    setSubmitted(false);
  }, [templateId]);

  useEffect(() => {
    if (open || locked) return;
    setHoverValue(null);
    setSelected(null);
    setComment('');
    setError(null);
  }, [locked, open]);

  const handleSubmit = async () => {
    if (selected == null || isSubmitting || locked) return;
    setSubmitting(true);
    setError(null);
    try {
      await onRate(templateId, selected, comment.trim() || undefined);
      setSubmitted(true);
      onSubmitted();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('already rated')) {
        setSubmitted(true);
        onSubmitted();
        return;
      }
      setError(message || t['com.streamlain.marketplace.rating.error']());
    } finally {
      setSubmitting(false);
    }
  };

  const subtitle =
    averageRating > 0
      ? t['com.streamlain.marketplace.rating.subtitle.with-average']({
          average: averageRating.toFixed(1),
        })
      : t['com.streamlain.marketplace.rating.subtitle.empty']();

  return (
    <Modal
      open={open}
      onOpenChange={value => {
        if (!value) onClose();
      }}
      title={t['com.streamlain.marketplace.rating.title']()}
      width={520}
    >
      <div className={styles.ratingDialogBody}>
        <p className={styles.ratingDialogSubtitle}>
          {blockedMessage
            ? blockedMessage
            : locked
              ? marketplaceCopy(
                  t,
                  'com.streamlain.marketplace.rating.already-submitted',
                  'Du hast diese Vorlage bereits bewertet.'
                )
              : subtitle}
        </p>
        <div
          className={styles.ratingStars}
          onMouseLeave={() => setHoverValue(null)}
        >
          {[1, 2, 3, 4, 5].map(value => (
            <button
              key={value}
              type="button"
              className={
                value <= display
                  ? styles.ratingStarActive
                  : styles.ratingStarInactive
              }
              onMouseEnter={() => setHoverValue(value)}
              onClick={() => {
                setSelected(value);
                setSubmitted(false);
              }}
              disabled={isSubmitting || locked}
              aria-label={String(value)}
            >
              <StarIcon size={26} />
            </button>
          ))}
        </div>
        <textarea
          className={styles.ratingComment}
          placeholder={t[
            'com.streamlain.marketplace.rating.comment.placeholder'
          ]()}
          value={comment}
          onChange={e => setComment(e.target.value)}
          disabled={isSubmitting || locked}
          rows={3}
          maxLength={1000}
        />
        {error && <div className={styles.manageError}>{error}</div>}
        <div className={styles.ratingFooter}>
          {submitted ? (
            <span className={styles.ratingSubmitted}>
              <CheckIcon size={14} />
              {t['com.streamlain.marketplace.rating.submitted']()}
            </span>
          ) : null}
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
          >
            {t['com.streamlain.marketplace.rating.close']()}
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={() => void handleSubmit()}
            disabled={selected == null || isSubmitting || locked}
          >
            {isSubmitting
              ? t['com.streamlain.marketplace.rating.submitting']()
              : t['com.streamlain.marketplace.rating.submit']()}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function managePrimaryActionLabel(
  t: ReturnType<typeof useI18n>,
  management: InstalledTemplateManagement
): string {
  if (management.primaryAction === 'open_doc') {
    return t['com.streamlain.marketplace.installed.action.open']();
  }
  if (management.primaryAction === 'open_workflow') {
    return t['com.streamlain.marketplace.installed.action.open-builder']();
  }
  if (management.primaryAction === 'use_step') {
    return t['com.streamlain.marketplace.installed.action.open-builder']();
  }
  if (management.primaryAction === 'open_apps') {
    return t['com.streamlain.marketplace.installed.action.open-apps']();
  }
  return '';
}

// Re-export prerequisite types for consumers importing from the wizard.
export type {
  ConfigPrerequisite,
  CredentialPrerequisite,
  TemplatePrerequisites,
} from './use-template-prerequisites';
